/* eslint-disable react/prop-types */
/**
 * NoteDetailDialog — the legacy note viewer/editor dialog, extracted VERBATIM
 * from App.js (Dashboard) as V2 Notes migration Phase 3A (Jun 2026).
 *
 * IMPORTANT: this is a pure mechanical lift. All state and handlers still
 * live in the legacy Dashboard and are passed through the single ctx bag
 * (134 entries, see destructure below). Do NOT add local state here without
 * checking the Dashboard call site in App.js. A future Phase 3B V2 host can
 * mount this dialog natively by providing the same ctx contract.
 */
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatMrn } from '../../utils/formatMrn';
import { formatLocalDate } from '../../utils/claimDisplay';
import { matrixBaseRows } from '../../utils/matrixRows';
import { AlertTriangle, ArrowLeft, ArrowRight, Ban, Building, CalendarPlus, Check, CheckCircle, ChevronDown, ChevronsUpDown, Columns2, Copy, Download, Edit, ExternalLink, Eye, FileText, History, Loader2, Lock, LogOut, MoreVertical, Plus, Printer, RefreshCw, RotateCcw, Send, Target, Trash2, Unlock, X } from "lucide-react";
import ReactDOM from "react-dom";
import { AssessmentQuickSend } from './AssessmentQuickSend';
import { toast } from "sonner";
import { ConsentAffirmationDisplay } from "../../components/consent/ConsentAffirmationDisplay";
import { ConsentLivePreview } from "../../components/consent/ConsentLivePreview";
import { DraftRecoveryBanner } from "../../components/DraftRecoveryModal";
import { AppointmentLinkSelect } from "../../components/features/AppointmentLinkSelect";
import FormFieldRenderer from "../../components/forms/FormFieldRenderer";
import { SaveIndicator } from "../../components/SaveIndicator";
import { AddonCptPicker } from "../../components/shared/AddonCptPicker";
import { CopayReminderBanner } from "../../components/shared/CopayReminderBanner";
import { CreateTaskFieldWidget } from "../../components/shared/CreateTaskFieldWidget";
import { MostRecentVitalsCard, AddVitalsDialog, parseVitalsSnapshot } from "../../components/shared/VitalsFieldWidget";
import { SignatureDisplay } from "../../components/shared/SignatureDisplay";
import { FormFileUploadField } from "../../components/shared/FormFileUploadField";
import { SignatureImage } from "../../components/shared/SignatureImage";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { DebouncedInput, DebouncedTextarea } from "../../components/ui/debounced-textarea";
import { Dialog, DialogContent, DialogHeader } from "../../components/ui/dialog.jsx";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { ReferenceNotePanel } from "../../ReferenceNotePanel";
import { getEffectivePosCodes, getOptionLabel, isSuperAdmin } from "../../utils/helpers";
import { fmtLocalDate } from "../../utils/insuranceDates";
import { extractNoteTextContent } from "../../utils/noteTextExtractor";
import { staticTextBorderStyle, staticTextImageClass } from "../../utils/staticTextStyle";

export const NoteDetailDialog = ({ ctx }) => {
  const {
    API,
    NOTE_SIZE_PRESETS,
    allClientsForLookup,
    appointments,
    autosaveBlockedReason,
    availablePANs,
    availableTreatmentPlanGoals,
    calculateAge,
    canEditNote,
    canLockNote,
    clientAppointments,
    clientDiagnoses,
    clientGroups,
    clientMedications,
    clientMismatchInfo,
    clientNotes,
    clientVitals,
    clients,
    closeNoteWithCheck,
    confirmAction,
    cptCodes,
    editContextMenuPos,
    editedNoteConsent,
    editedNoteContent,
    editingNote,
    fetchAppointments,
    fetchTreatmentPlanGoals,
    formTemplates,
    formatDateMMDDYYYY,
    formatLocalDateTime,
    formatTimeTo12Hour,
    getAccessibleCPTCodes,
    getSignatureUrl,
    handleAddLinkedForm,
    handleCloseEditingNoteDiscard,
    handleCloseEditingNoteKeepEditing,
    handleCloseEditingNoteSaveAndClose,
    handleFieldChangeLifted,
    handleMismatchKeepEditing,
    handleMismatchMoveNote,
    handleMismatchSaveAndClose,
    handleNavigateToNote,
    handleSignNote,
    handleUnsignNote,
    isEditingNote,
    isSavingNote,
    linkedFormsAdded,
    linkedGoals,
    loadedSignatures,
    locations,
    noteCreationError,
    noteDialogSize,
    noteEditingAutosave,
    noteHasClientVisibilityDivider,
    noteJustSigned,
    noteLinkedAppointment,
    notes,
    openNote,
    organizations,
    posCodes,
    providers,
    refreshClientNotes,
    refreshNotes,
    refreshTodos,
    replaceClientVariables,
    returnToCreateNote,
    selectedClient,
    selectedNote,
    serviceTypes,
    setActiveTab,
    setAppointmentFormData,
    setAppointments,
    setAutosaveBlockedReason,
    setClientMedications,
    setClientNotes,
    setCorrectionsReason,
    setEditContextMenuPos,
    setEditedNoteConsent,
    setEditedNoteContent,
    setEditingAppointment,
    setEditingMedication,
    setMedPlanCapture,
    setMedSnapshotCapture,
    setIsEditingNote,
    setIsSavingNote,
    setLinkedFormsAdded,
    setLinkedGoals,
    setLoadedSignatures,
    setNewNoteFormData,
    setNewNoteFormDataBridge,
    setNotBillableComment,
    setNotBillableReason,
    setNotBillableTargetNote,
    setNoteCreationError,
    setNoteJustSigned,
    setNoteLinkedAppointment,
    setNotes,
    setReturnToCreateNote,
    setSelectedClient,
    setSelectedNote,
    setSelectedNoteClients,
    setSelectedNoteProvider,
    setSelectedNoteStatus,
    setSelectedTemplate,
    setSendToClientNote,
    setShowAddNote,
    setShowAllDiagnoses,
    setShowAppointmentForm,
    setShowCloseEditingNoteWarning,
    setShowCorrectionsDialog,
    setShowDiagnosisForm,
    setShowEditContextMenu,
    setShowLinkGoalsDialog,
    setShowMedicationForm,
    setShowNextStepsDialog,
    setShowNotBillableDialog,
    setShowNoteDetail,
    setShowNoteHistory,
    setShowReferencePanel,
    setShowSendToClientDialog,
    setShowSignatureContextMenu,
    setSignatureContextMenuPos,
    setSignatureContextMenuType,
    showAllDiagnoses,
    showCloseEditingNoteWarning,
    showEditContextMenu,
    showNoteDetail,
    showReferencePanel,
    showSignatureContextMenu,
    signatureContextMenuPos,
    signatureContextMenuType,
    updateNoteDialogSize,
    updateNoteStatusOptimistically,
    user,
    userRoleName,
    users,
    // Optional (V2 only): opens the date-picking signature dialog from the
    // signature right-click menu ("Sign with custom date/time…").
    openSignatureConfirm,
  } = ctx;

  // ── Content-loss override (Jun 2026 user request) ──────────────────
  // When autosave is blocked by the >60% content-loss guard, the Save
  // button opens a confirmation instead of being dead — the user can
  // consciously "Save anyway" (audited server-side). Local UI state
  // only; no Dashboard coordination needed.
  const [showLossOverride, setShowLossOverride] = useState(false);
  const [lossOverridePercent, setLossOverridePercent] = useState(null);
  const navigate = useNavigate();
  // Jun 2026 (user report): notes authored by a since-archived provider
  // showed "Provider: Not assigned" in the header — /users excludes
  // archived users, so the list lookup missed. Resolve the name directly
  // from GET /users/{id} (returns archived users) when the list misses.
  const [resolvedProviderCache, setResolvedProviderCache] = useState({});
  // Editor-only meta flags that renderers stash via setNewNoteFormData but
  // that are NOT note fields (e.g. pan_select's `${id}__custom_mode`
  // marker). The content bridge drops unknown keys, so without this the
  // "Write custom need..." option was a no-op in the V2 editor (Jun 2026).
  const [editorMetaFlags, setEditorMetaFlags] = useState({});
  const setNewNoteFormDataWithMeta = useCallback((updater) => {
    if (typeof updater !== 'function') return;
    setEditorMetaFlags(prevFlags => {
      const probe = updater({ ...prevFlags }) || {};
      const nextFlags = {};
      Object.keys(probe).forEach(k => {
        if (k.endsWith('__custom_mode')) nextFlags[k] = probe[k];
      });
      return nextFlags;
    });
    if (typeof setNewNoteFormDataBridge === 'function') setNewNoteFormDataBridge(updater);
  }, [setNewNoteFormDataBridge]);
  const headerMissingProviderId =
    selectedNote?.provider_id && !(users || []).some((u) => u.id === selectedNote.provider_id)
      ? selectedNote.provider_id : null;

  // Jun 2026 — unsign an inline comp-assessment review (edit access only).
  const handleUnsignReview = async (review) => {
    if (!window.confirm('Remove the signature from this review?\n\nThe review will return to an editable (and deletable) state.')) return;
    try {
      await axios.post(`${API}/notes/${selectedNote.id}/reviews/${review.id}/unsign`);
      const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
      setSelectedNote(updatedNote.data);
      toast.success('Signature removed from review');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove signature');
    }
  };
  useEffect(() => {
    if (!headerMissingProviderId) return;
    if (Object.prototype.hasOwnProperty.call(resolvedProviderCache, headerMissingProviderId)) return;
    let cancelled = false;
    axios.get(`${API}/users/${headerMissingProviderId}`)
      .then((r) => {
        if (!cancelled) {
          setResolvedProviderCache((prev) => ({ ...prev, [headerMissingProviderId]: r.data || null }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedProviderCache((prev) => ({ ...prev, [headerMissingProviderId]: null }));
        }
      });
    return () => { cancelled = true; };
  }, [headerMissingProviderId]);
  const isContentLossBlock = autosaveBlockedReason === 'Content Loss Detected - Save Blocked';
  useEffect(() => {
    const h = (e) => setLossOverridePercent(e?.detail?.lossPercent ?? null);
    window.addEventListener('autosave-catastrophic-loss', h);
    return () => window.removeEventListener('autosave-catastrophic-loss', h);
  }, []);
  // Recovery: if the editor is holding a stale optimistic placeholder id or
  // the draft was deleted server-side, re-create the note so the user's work
  // is saved instead of failing with "Note not found" until a page refresh.
  const recreateNoteFromEditor = async (updateData) => {
    const payload = {
      client_id: selectedNote.client_id,
      client_ids: selectedNote.client_ids || [],
      is_group_note: selectedNote.is_group_note || false,
      provider_id: selectedNote.provider_id,
      template_id: selectedNote.template_id,
      note_type: selectedNote.note_type,
      form_type_name: selectedNote.form_type_name || selectedNote.form_type,
      title: selectedNote.title || null,
      status: 'draft',
      billable: selectedNote.billable || false,
      ...updateData,
    };
    const res = await axios.post(`${API}/notes`, payload);
    // The server may dedup and return an EXISTING recent draft (same client/
    // template/DOS within 60s) without applying our content — always follow
    // up with a PUT so the user's edits land on whichever note came back.
    await axios.put(`${API}/notes/${res.data.id}`, updateData);
    return res.data;
  };

  // Save with automatic recovery (optimistic id / deleted draft → re-create).
  // Returns the id the content now lives under.
  const saveNoteContentWithRecovery = async (updateData) => {
    let activeNoteId = selectedNote.id;
    const isOptimisticId = typeof activeNoteId === 'string' && activeNoteId.startsWith('optimistic-');
    if (isOptimisticId) {
      const recreated = await recreateNoteFromEditor(updateData);
      activeNoteId = recreated.id;
    } else {
      try {
        await axios.put(`${API}/notes/${activeNoteId}`, updateData);
      } catch (err) {
        if (err.response?.status === 404) {
          const recreated = await recreateNoteFromEditor(updateData);
          activeNoteId = recreated.id;
          toast.info('Original draft was missing — your note was saved as a new draft');
        } else {
          throw err;
        }
      }
    }
    return activeNoteId;
  };

  const performOverrideSave = async () => {
    setShowLossOverride(false);
    setIsSavingNote(true);
    try {
      const updateData = {
        content: JSON.stringify(editedNoteContent),
        date_of_service: selectedNote.date_of_service,
        start_time: selectedNote.start_time,
        end_time: selectedNote.end_time,
        units: selectedNote.units,
        cpt_code: selectedNote.cpt_code,
        cpt_code_id: selectedNote.cpt_code_id,
        addon_cpt_code_ids: Array.isArray(selectedNote.addon_cpt_code_ids)
          ? selectedNote.addon_cpt_code_ids : [],
        appointment_id: selectedNote.appointment_id,
        linked_goals: linkedGoals,
        ...(JSON.stringify(editedNoteConsent || null) !== JSON.stringify(selectedNote.telehealth_consent || null)
          ? { telehealth_consent: editedNoteConsent }
          : {}),
        content_loss_override: true,
        updated_at: new Date().toISOString(),
      };
      const savedNoteId = await saveNoteContentWithRecovery(updateData);
      toast.success('Note saved — large content change recorded');
      setNoteCreationError(null);
      setAutosaveBlockedReason(null);
      setLossOverridePercent(null);
      setIsEditingNote(false);
      setEditedNoteConsent(null);
      noteEditingAutosave.clearLocalDraft?.();
      refreshNotes(); refreshTodos();
      const updatedNote = await axios.get(`${API}/notes/${savedNoteId}`);
      setSelectedNote(updatedNote.data);
      if (updatedNote.data?.current_version) {
        noteEditingAutosave.setVersion?.(updatedNote.data.current_version);
      }
    } catch (error) {
      setNoteCreationError(`Failed to save: ${error.response?.data?.detail || error.message}`);
      toast.error(`Failed to save: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSavingNote(false);
    }
  };

  return (
    <>
      {showNoteDetail && (
      <Dialog 
        modal={false}
        allowOverlayClose={user?.is_billing_manager || user?.role === 'super admin' || user?.role === 'office'}
        open={showNoteDetail}
        onOpenChange={(open) => {
        void 0 && console.log('[Dialog onOpenChange] CALLED - open:', open);
        
        const saveStatus = noteEditingAutosave?.saveStatus;
        const isSaving = saveStatus === 'saving';
        // EXIT GUARD: Use hasRealUnsavedChanges to check for meaningful differences
        // This prevents false "unsaved changes" warnings when content matches server
        // CRITICAL: Fall back to false, not hasUnsavedChanges flag, to prevent false positives
        const hasRealChanges = noteEditingAutosave?.hasRealUnsavedChanges?.() ?? false;
        
        // If closing and there are REAL unsaved changes OR save is in progress, show the custom warning modal
        if (!open && isEditingNote && (hasRealChanges || isSaving)) {
          // Use setTimeout to break out of Radix Dialog's synchronous event handling
          setTimeout(() => {
            setShowCloseEditingNoteWarning(true);
          }, 0);
          return; // Don't close yet - wait for modal response
        }
        
        // CRITICAL: Check for ERROR or OFFLINE_CACHED states - data NOT synced to server!
        if (!open && isEditingNote && (saveStatus === 'error' || saveStatus === 'offline_cached')) {
          const shouldClose = window.confirm(
            '⚠️ WARNING: Your changes are NOT saved on the server!\n\n' +
            'Your internet connection may be lost or there was a server error.\n\n' +
            'If you close this note now, YOUR WORK WILL BE LOST.\n\n' +
            'Please copy your work before closing, or click Cancel and try the Retry button.'
          );
          if (!shouldClose) {
            return; // Don't close
          }
        }
        
        if (!open && noteJustSigned) {
          // Note is being closed and was just signed - show next steps
          setShowNextStepsDialog(true);
          setNoteJustSigned(false);
        }
        
        // SAVE TEXT BACKUP before clearing state - this is the CRITICAL path for normal close
        if (!open && selectedNote?.id && editedNoteContent) {
          void 0 && console.log('[Dialog onOpenChange] Creating text backup before close');
          try {
            const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
            void 0 && console.log('[Dialog onOpenChange] Extracted text length:', textContent?.length);
            if (textContent && textContent.trim().length > 0) {
              void 0 && console.log('[Dialog onOpenChange] Sending backup for note:', selectedNote.id);
              axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
                text_content: textContent,
                client_id: selectedNote.client_id,
                client_name: selectedNote.client_name,
                template_name: selectedNote.template_name || selectedNote.form_name
              }, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              }).then(res => {
                void 0 && console.log('[Dialog onOpenChange] Backup saved successfully:', res.data);
              }).catch(err => {
                console.warn('[Dialog onOpenChange] Failed to save text backup:', err);
              });
            } else {
              void 0 && console.log('[Dialog onOpenChange] Text content empty, skipping backup');
            }
          } catch (e) {
            console.warn('[Dialog onOpenChange] Error creating text backup:', e);
          }
        } else if (!open) {
          void 0 && console.log('[Dialog onOpenChange] Skipping backup - selectedNote:', selectedNote?.id, 'editedNoteContent:', !!editedNoteContent);
        }
        
        // When dialog is closing, clear the note state SYNCHRONOUSLY to prevent flash
        // IMPORTANT: Don't clear selectedNote if the Next Steps dialog is opening —
        // its click handlers need selectedNote to update the note status.
        if (!open) {
          // Clear state immediately to prevent flash-back on re-render
          setEditedNoteContent(null);
          setEditedNoteConsent(null);
          setLinkedGoals([]);
          setIsEditingNote(false);
          setShowReferencePanel(false);
          if (!noteJustSigned) {
            setSelectedNote(null);
          }
          // CRITICAL: Clear error states when closing to prevent carryover
          setNoteCreationError(null);
          setAutosaveBlockedReason(null);
        }
        
        setShowNoteDetail(open);
      }}>
        <DialogContent 
          key={selectedNote?.id || 'no-note'}
          hideCloseButton={true}
          className={`max-h-[95vh] max-w-[95vw] overflow-y-auto ${showReferencePanel ? 'ref-panel-open' : ''}`}
          style={showReferencePanel 
            ? { width: '53vw', left: '27.5vw', transform: 'translate(-50%, -50%)' } 
            : { width: NOTE_SIZE_PRESETS[noteDialogSize]?.width || '95vw' }}
          onEscapeKeyDown={(e) => {
            // iter516: blur first so a focused DebouncedTextarea commits its
            // last keystrokes, then close via the normal check path on the
            // next tick — Radix's default close cleared editor state before
            // the flush could see the committed value (sub-100ms edits lost).
            e.preventDefault();
            try { document.activeElement?.blur?.(); } catch { /* noop */ }
            setTimeout(() => closeNoteWithCheck(), 80);
          }}
          onPointerDownOutside={(e) => {
            const target = e.target;
            // Allow Select/Combobox dropdown interactions (they render via portal outside the dialog)
            const isRadixPortalContent = 
              target.closest('[data-radix-select-content]') || 
              target.closest('[data-radix-select-viewport]') ||
              target.closest('[data-radix-select-item]') ||
              target.closest('[role="listbox"]') ||
              target.closest('[role="option"]') ||
              target.closest('[data-radix-popper-content-wrapper]') ||
              target.closest('[data-radix-collection-item]') ||
              target.closest('[data-radix-portal]') ||
              target.closest('.select-content');
              
            if (isRadixPortalContent) {
              e.preventDefault();
              return;
            }
            
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onWheel={(e) => {
            // Keep wheel/trackpad events from bubbling to react-remove-scroll's
            // document listener, which can cancel diagonal trackpad deltas and
            // make the note feel unscrollable (Jun 2026).
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            e.stopPropagation();
          }}
          onContextMenu={(e) => {
            // Check if this right-click is on a signature area (has specific handler)
            const isSignatureArea = e.target.closest('[data-signature-area]') || 
                                    e.target.closest('.cursor-pointer[title="Right-click for options"]');
            
            if (isSignatureArea) {
              // Let the signature area's onContextMenu handle it
              return;
            }
            
            if (isEditingNote) {
              // Allow native context menu for input fields (copy/paste)
              const tagName = e.target.tagName.toLowerCase();
              const isInputField = tagName === 'input' || tagName === 'textarea' || 
                                   e.target.isContentEditable ||
                                   e.target.closest('[contenteditable="true"]') ||
                                   e.target.closest('input') ||
                                   e.target.closest('textarea');
              
              if (isInputField) {
                // Let native browser context menu appear for copy/paste
                return;
              }
              
              e.preventDefault();
              setEditContextMenuPos({ x: e.clientX, y: e.clientY });
              setShowEditContextMenu(true);
            }
          }}
        >
          {/* A11y: Radix requires a DialogTitle + description; visually hidden
              (the visible header is custom-rendered further down). */}
          <DialogPrimitive.Title className="sr-only">
            {selectedNote?.template_name || 'Clinical note'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Note editor dialog
          </DialogPrimitive.Description>
          {/* Close Editing Note Warning Modal - Rendered INSIDE DialogContent like Create Note */}
          {showCloseEditingNoteWarning && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center">
              {/* Backdrop */}
              <div 
                className="absolute inset-0 bg-black/50"
                onClick={handleCloseEditingNoteKeepEditing}
              />
              {/* Modal */}
              <div className="relative bg-white rounded-lg shadow-2xl p-6 max-w-md mx-4 z-10">
                <h2 className="text-lg font-semibold mb-2">Unsaved Changes</h2>
                <p className="text-gray-600 mb-4">
                  You have unsaved changes. Your changes are being auto-saved, but the latest edits may not be saved yet.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={handleCloseEditingNoteKeepEditing}
                    data-testid="unsaved-keep-editing-btn"
                  >
                    Keep Editing
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCloseEditingNoteDiscard}
                    data-testid="unsaved-close-without-saving-btn"
                  >
                    Close Without Saving
                  </Button>
                  <Button
                    onClick={handleCloseEditingNoteSaveAndClose}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="unsaved-save-and-close-btn"
                  >
                    Save & Close
                  </Button>
                </div>
              </div>
            </div>
          )}
          {/* Client Mismatch Dialog — prevents cross-contamination */}
          {clientMismatchInfo && (
            <div className="fixed inset-0 z-[9999999] flex items-center justify-center" data-testid="client-mismatch-dialog">
              <div className="absolute inset-0 bg-black/60" />
              <div className="relative bg-white rounded-lg shadow-2xl p-6 max-w-lg mx-4 z-10 border-2 border-amber-400">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <h2 className="text-lg font-semibold">You navigated to a different client</h2>
                </div>
                <p className="text-gray-600 mb-1">
                  You're editing a <strong>{clientMismatchInfo.templateName}</strong> for <strong>{clientMismatchInfo.prevName}</strong>,
                  but you just opened <strong>{clientMismatchInfo.newName}</strong>'s chart.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Your work is safe — nothing has been lost. What would you like to do?
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={handleMismatchKeepEditing}
                    className="bg-teal-600 hover:bg-teal-700 text-white justify-start"
                    data-testid="mismatch-keep-editing"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Go back to {clientMismatchInfo.prevName}'s chart and keep editing
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleMismatchSaveAndClose}
                    className="justify-start"
                    data-testid="mismatch-save-close"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save note to {clientMismatchInfo.prevName} and close it
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleMismatchMoveNote}
                    className="justify-start border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="mismatch-move-note"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Move this note to {clientMismatchInfo.newName} instead
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  All changes are auto-saved. Your work will not be lost regardless of which option you choose.
                </p>
              </div>
            </div>
          )}
          <DialogHeader className="p-0 space-y-0">
            {/* Navigation Row (Prev/Next + X) OR a plain close-button row.
                Jun 2026 fix: both are computed in ONE expression so the X
                can never disappear — previously the two blocks used
                divergent conditions and the V2 note host (/v2/note/:id)
                rendered NEITHER, leaving no way to close the note. */}
            {(() => {
              const navRow = (selectedNote && selectedClient) ? (() => {
                // Use the client's FULL note history (`clientNotes` from context) —
                // NOT the global 30-day `notes` list — so navigation shows every
                // note for this client, not just the recent window. Falls back
                // to the 30-day filter on the rare case the context list hasn't
                // loaded yet (chart still opening).
                const haveContext = Array.isArray(clientNotes) && clientNotes.length > 0;
                const fallback = Array.isArray(notes)
                  ? notes.filter(n => n.client_id === selectedClient.id)
                  : [];
                const source = haveContext ? clientNotes : fallback;
                if (source.length <= 1) return null;

                const clientNotesForNav = source.slice().sort(
                  (a, b) => new Date(b.date_of_service || b.created_at) - new Date(a.date_of_service || a.created_at)
                );

                const currentIndex = clientNotesForNav.findIndex(n => n.id === selectedNote.id);
                if (currentIndex === -1) return null;

                return (
                  <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={currentIndex === 0}
                      onClick={() => handleNavigateToNote(clientNotesForNav[currentIndex - 1])}
                      className="gap-1"
                      data-testid="prev-note-button"
                    >
                      <ArrowLeft className="h-3 w-3" />
                      Prev Note
                    </Button>
                    <span className="text-sm text-gray-600 font-medium">
                      Note {currentIndex + 1} of {clientNotesForNav.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={currentIndex === clientNotesForNav.length - 1}
                        onClick={() => handleNavigateToNote(clientNotesForNav[currentIndex + 1])}
                        className="gap-1"
                        data-testid="next-note-button"
                      >
                        Next Note
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => closeNoteWithCheck()}
                        className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900"
                        data-testid="close-note-button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })() : null;

              if (navRow) return navRow;

              // No navigation possible — always show a bare close row.
              return (
                <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => closeNoteWithCheck()}
                    className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900"
                    data-testid="close-note-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })()}
          </DialogHeader>
          
          {/* Copay reminder for providers (Jun 2026) — skipped when
              already collected for this DOS or client has no copay */}
          {selectedNote?.client_id && (
            <div className="px-4 pt-2">
              <CopayReminderBanner
                clientId={selectedNote.client_id}
                date={String(selectedNote.date_of_service || '').slice(0, 10) || undefined}
              />
            </div>
          )}

          {/* Back to Parent Note Button - shown when editing a linked form */}
          {returnToCreateNote && (
            <div className="px-4 py-2 border-b bg-blue-50">
              <Button
                size="sm"
                variant="outline"
                className="bg-white hover:bg-blue-100 text-blue-700 border-blue-300"
                onClick={() => {
                  setSelectedTemplate(returnToCreateNote.template);
                  setSelectedNoteClients(returnToCreateNote.clients);
                  setSelectedNoteProvider(returnToCreateNote.provider);
                  setSelectedNoteStatus(() => returnToCreateNote.status);
                  setNewNoteFormData(() => returnToCreateNote.formData);
                  setLinkedFormsAdded(() => returnToCreateNote.linkedFormsAdded);
                  setShowNoteDetail(() => false);
                  setSelectedNote(() => null);
                  setIsEditingNote(() => false);
                  setShowAddNote(() => true);
                  setReturnToCreateNote(() => null);
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to {returnToCreateNote.parentTemplateName}
              </Button>
            </div>
          )}
          
          {/* PROMINENT Corrections Banner - shown at top when note needs corrections */}
          {selectedNote?.status === 'needs_corrections' && (
            <div className="mt-3 p-4 bg-orange-100 border-2 border-orange-400 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-800 text-lg">Corrections Requested</h4>
                  {selectedNote.needs_corrections_reason ? (
                    <p className="text-orange-700 mt-1">{selectedNote.needs_corrections_reason}</p>
                  ) : (
                    <p className="text-orange-600 mt-1 italic">No specific reason was provided.</p>
                  )}
                  {selectedNote.corrections_requested_at && (
                    <p className="text-orange-600 text-sm mt-2">
                      Requested on {new Date(selectedNote.corrections_requested_at).toLocaleDateString()}
                      {selectedNote.corrections_requested_by_name && ` by ${selectedNote.corrections_requested_by_name}`}
                    </p>
                  )}
                  <p className="text-orange-800 text-sm mt-3 font-medium bg-orange-200 rounded px-3 py-1.5 inline-block">
                    To clear this: make your corrections, then mark the note as "Ready for Billing" to resubmit.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* PROMINENT Non-Billable Banner — Feb 2026.  Shown at top
              when a biller has terminally marked the note non-billable
              (timely-filing expired, no coverage, missing auth, etc.).
              Includes a "Restore to Billable" button that puts the
              note back into the biller queue, restoring the prior
              status from the audit snapshot stored on the doc. */}
          {selectedNote?.status === 'not_billable' && (
            <div
              className="mt-3 p-4 bg-red-100 border-2 border-red-400 rounded-lg"
              data-testid="note-not-billable-banner"
            >
              <div className="flex items-start gap-3">
                <Ban className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-800 text-lg">Note Marked Non-Billable</h4>
                  {selectedNote.not_billable_reason && (
                    <p className="text-red-700 mt-1">
                      Reason: <span className="font-medium">
                        {{
                          timely_filing_expired: 'Timely Filing Expired',
                          no_active_insurance: 'No Active Insurance Coverage',
                          non_covered_service: 'Non-Covered Service',
                          missing_prior_authorization: 'Missing Prior Authorization',
                          administrative_writeoff: 'Administrative Write-off',
                          duplicate_note: 'Duplicate of Another Note',
                          cpt_restricted_for_insurance: 'CPT Restricted for This Insurance',
                          other: 'Other',
                        }[selectedNote.not_billable_reason] || selectedNote.not_billable_reason}
                      </span>
                    </p>
                  )}
                  {selectedNote.not_billable_comment && (
                    <p className="text-red-700 mt-1 text-sm italic">
                      "{selectedNote.not_billable_comment}"
                    </p>
                  )}
                  {(selectedNote.not_billable_by_name || selectedNote.not_billable_at) && (
                    <p className="text-red-600 text-sm mt-2">
                      Marked
                      {selectedNote.not_billable_at
                        && ` on ${new Date(selectedNote.not_billable_at).toLocaleString()}`}
                      {selectedNote.not_billable_by_name
                        && ` by ${selectedNote.not_billable_by_name}`}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-50"
                      data-testid="note-restore-from-not-billable-btn"
                      onClick={async () => {
                        if (!selectedNote?.id) return;
                        const confirmRestore = window.confirm(
                          'Restore this note to the billing queue?\n\n'
                          + 'The note will return to its prior status (typically '
                          + '"Ready for Billing") so you can re-attempt the claim.'
                        );
                        if (!confirmRestore) return;
                        try {
                          const resp = await axios.post(
                            `${API}/notes/${selectedNote.id}/restore-from-not-billable`,
                            {},
                          );
                          const newStatus = resp.data?.new_status || 'signed_ready_for_billing';
                          toast.success(`Note restored to "${newStatus.replace(/_/g, ' ')}".`);
                          // Optimistic local update so the banner disappears
                          // and the note re-enters the relevant queues.
                          const restoredNote = {
                            ...selectedNote,
                            status: newStatus,
                            not_billable_at: null,
                            not_billable_by: null,
                            not_billable_by_name: null,
                            not_billable_reason: null,
                            not_billable_comment: null,
                            not_billable_prior_status: null,
                          };
                          setSelectedNote(restoredNote);
                          setNotes((prev) => prev.map(
                            (n) => (n.id === selectedNote.id ? { ...n, ...restoredNote } : n),
                          ));
                          window.dispatchEvent(new CustomEvent('noteStatusChanged', {
                            detail: {
                              noteId: selectedNote.id,
                              newStatus,
                              noteData: restoredNote,
                            },
                          }));
                        } catch (err) {
                          const detail = err.response?.data?.detail
                            || 'Failed to restore note.';
                          toast.error(typeof detail === 'string' ? detail : 'Failed to restore note.');
                        }
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                      Restore to Billable
                    </Button>
                    <span className="text-xs text-red-600">
                      Returns the note to the biller queue.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* CRITICAL: Save Error Banner for Editing Existing Notes - Shows when autosave fails */}
          {/* STICKY: Fixed to top of dialog so it's visible even when scrolled to bottom of long note */}
          {isEditingNote && noteCreationError && (
            <div className="sticky top-0 z-50 bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4 flex-shrink-0 shadow-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5 animate-pulse" />
                <div className="flex-1">
                  <h3 className="font-bold text-red-800 text-lg">NOTE NOT SAVED!</h3>
                  <p className="text-red-700 mt-1">{noteCreationError}</p>
                  <p className="text-red-600 text-sm mt-2">
                    Your work is still here. Please copy your text below before closing, or try saving again.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-100"
                      onClick={() => {
                        // Build header info for recovery context
                        const noteClient = allClientsForLookup.find(c => c.id === selectedNote?.client_id);
                        const clientName = noteClient?.full_name || selectedNote?.client_name || 'Unknown Client';
                        const clientMRN = noteClient?.mrn || 'No MRN';
                        const dateStr = selectedNote?.date_of_service || new Date().toISOString().split('T')[0];
                        const templateName = selectedNote?.template_name || selectedNote?.form_name || 'Unknown Template';
                        const providerName = users?.find(u => u.id === selectedNote?.provider_id)?.full_name || 'Unknown Provider';
                        
                        // Build full recovery text with header
                        const headerText = `=== NOTE RECOVERY DATA ===
Client: ${clientName}
MRN: ${clientMRN}
Date: ${dateStr}
Template: ${templateName}
Provider: ${providerName}
Note ID: ${selectedNote?.id || 'Unknown'}
===========================

`;
                        // Copy all form data to clipboard
                        let formDataText = '';
                        if (editedNoteContent?.fields) {
                          formDataText = editedNoteContent.fields
                            .filter(field => field?.value)
                            .map(field => `${field.label || field.id}: ${typeof field.value === 'object' ? JSON.stringify(field.value) : field.value}`)
                            .join('\n\n');
                        } else if (editedNoteContent?.sections) {
                          formDataText = editedNoteContent.sections
                            .flatMap(section => section.fields || [])
                            .filter(field => field?.value)
                            .map(field => `${field.label || field.id}: ${typeof field.value === 'object' ? JSON.stringify(field.value) : field.value}`)
                            .join('\n\n');
                        }
                        
                        navigator.clipboard.writeText(headerText + formDataText);
                        toast.success('Note content copied to clipboard (includes client name & date)');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy All Content
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-100"
                      onClick={() => setNoteCreationError(null)}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {selectedNote && (() => {
            // Find the client for this note - check multiple sources
            // Priority when viewing from client chart: 
            //   1) selectedClient (the chart we're in)
            //   2) note's embedded client_name (from backend)
            //   3) allClientsForLookup by client_id
            // This ensures when opening notes from a client chart, we ALWAYS show that client
            let noteClient = null;
            
            // If we're viewing from a client chart and the note belongs to this client, use selectedClient
            if (selectedClient && selectedNote.client_id === selectedClient.id) {
              noteClient = selectedClient;
            }
            
            // If not viewing from client chart context, try lookup arrays
            if (!noteClient && selectedNote.client_id) {
              noteClient = allClientsForLookup.find(c => c.id === selectedNote.client_id);
            }
            
            // If still not found but note has client_name from backend, create minimal object for display
            // Include assigned_providers from the note if available for diagnosis dialog
            if (!noteClient && selectedNote.client_id && selectedNote.client_name) {
              noteClient = { 
                id: selectedNote.client_id, 
                full_name: selectedNote.client_name,
                assigned_providers: selectedNote.assigned_providers || [],
                _fromNoteFallback: true
              };
            }
            
            const noteProvider = users.find(u => u.id === selectedNote.provider_id)
              || resolvedProviderCache[selectedNote.provider_id]
              || null;
            
            // Parse note to get template info
            let templateName = null;
            let isBillable = false;
            try {
              const noteData = JSON.parse(selectedNote.content);
              if (noteData.template_name) {
                templateName = noteData.template_name;
                // Check if the template is billable
                const template = formTemplates.find(t => t.id === noteData.template_id);
                isBillable = template?.billable || selectedNote.billable || false;
              }
            } catch (e) {
              isBillable = selectedNote.billable || false;
            }
            
            // Get template for options
            const noteTemplate = formTemplates.find(t => t.id === selectedNote.template_id);
            
            // Only show "No Client Assigned" if the note truly has no client_id
            // Don't show it just because we can't find the client in our lookup arrays
            const hasNoClient = !selectedNote.client_id && !selectedNote.is_group_note;
            
            // Get primary diagnosis for display
            const getPrimaryDiagnosis = () => {
              if (!noteClient?.id) return null;
              const noteDiagnoses = clientDiagnoses?.filter(d => d.client_id === noteClient.id && d.status === 'active') || [];
              const primary = noteDiagnoses.find(d => d.is_primary) || noteDiagnoses[0];
              return primary ? `${primary.code} ${primary.description?.split(' ').slice(0, 3).join(' ')}${primary.description?.split(' ').length > 3 ? '...' : ''}` : null;
            };
            
            return (
              <div className="space-y-0">
                {/* Compact Note Header Row - with teal gradient background */}
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-b border-teal-200">
                  <div className="flex items-center justify-between px-4 py-3 sticky top-0 z-10">
                  <div className="flex items-center gap-3">
                    {/* Note Type */}
                    <span className="font-semibold text-gray-900">
                      {templateName || selectedNote.title || 'Clinical Note'}
                    </span>
                    {/* View Only indicator */}
                    {!canEditNote(selectedNote) && selectedNote.status !== 'locked' && !selectedNote.is_locked && (
                      <Badge className="bg-gray-100 text-gray-600 text-xs">
                        <Eye className="h-3 w-3 mr-1" />
                        View Only
                      </Badge>
                    )}
                    {/* Save Status Indicator */}
                    {isEditingNote && (
                      <>
                        <SaveIndicator 
                          status={noteEditingAutosave.saveStatus}
                          lastSavedAt={noteEditingAutosave.lastSavedAt}
                        />
                        {/* Save indicator now handled by noteEditingAutosave above */}
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Note Size Presets */}
                    {!showReferencePanel && (
                      <div className="flex items-center bg-gray-100 rounded-md p-0.5" data-testid="note-size-presets">
                        {Object.entries(NOTE_SIZE_PRESETS).map(([key, preset]) => (
                          <button
                            key={key}
                            onClick={() => updateNoteDialogSize(key)}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                              noteDialogSize === key 
                                ? 'bg-white text-teal-700 shadow-sm' 
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title={`${preset.label} width`}
                            data-testid={`note-size-${key}`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Reference Note Toggle */}
                    <Button 
                      size="sm" 
                      variant={showReferencePanel ? "default" : "outline"}
                      className={`h-8 px-3 text-xs ${showReferencePanel ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}
                      onClick={() => {
                        setShowReferencePanel(!showReferencePanel);
                      }}
                      data-testid="reference-note-toggle"
                      title="View another note side-by-side for reference"
                    >
                      <Columns2 className="h-3.5 w-3.5 mr-1" />
                      Reference
                    </Button>
                    {/* Options Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="h-8 px-2" data-testid="note-kebab-menu">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {/* Full Screen */}
                        <DropdownMenuItem onClick={() => {
                            const noteClientFS = allClientsForLookup.find(c => c.id === selectedNote.client_id);
                            const noteProviderFS = users.find(u => u.id === selectedNote.provider_id);
                            const linkedAppointmentFS = selectedNote.appointment_id ? (appointments.find(a => a.id === selectedNote.appointment_id) || noteLinkedAppointment) : null;
                            const aptCPTFS = linkedAppointmentFS?.cpt_code_id ? cptCodes.find(c => c.id === linkedAppointmentFS.cpt_code_id) : null;
                            const aptLocationFS = linkedAppointmentFS?.location_id ? locations.find(l => l.id === linkedAppointmentFS.location_id) : null;
                            
                            // Get primary diagnosis
                            let primaryDxFS = '';
                            if (selectedNote.diagnosis_snapshot?.primary) {
                              const dx = selectedNote.diagnosis_snapshot.primary;
                              primaryDxFS = `${dx.code} - ${dx.description}`;
                            } else if (noteClientFS?.id) {
                              const dx = clientDiagnoses.find(d => d.client_id === noteClientFS.id && d.is_primary && d.is_active);
                              if (dx) primaryDxFS = `${dx.diagnosis_code} - ${dx.diagnosis_description}`;
                            }
                            
                            // Format appointment info
                            let appointmentInfoFS = 'None linked';
                            if (linkedAppointmentFS) {
                              const aptDate = new Date(linkedAppointmentFS.start_time).toLocaleDateString();
                              const aptTime = new Date(linkedAppointmentFS.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              appointmentInfoFS = `${aptDate} ${aptTime}`;
                              if (aptCPTFS) appointmentInfoFS += ` | ${aptCPTFS.code} - ${aptCPTFS.name || aptCPTFS.description}`;
                              if (aptLocationFS) appointmentInfoFS += ` | ${aptLocationFS.name}`;
                            }
                            
                            let templateNameFS = 'Clinical Note';
                            let noteContentFS = '';
                            try {
                              const noteData = JSON.parse(selectedNote.content);
                              templateNameFS = noteData.template_name || 'Clinical Note';
                              
                              const renderField = (field) => {
                                const label = field.label || field.name || field.id || '';
                                const ftype = field.type || '';
                                
                                // Section headers render as dividers
                                if (ftype === 'section_header') {
                                  if (!label) return '<hr style="margin: 20px 0; border: none; border-top: 1px solid #e5e7eb;">';
                                  return '<h3>' + label + '</h3>';
                                }
                                
                                const value = field.value || field.default_value || '';
                                const options = field.options || [];
                                
                                // Single checkbox
                                if (ftype === 'checkbox') {
                                  const checked = value === true || value === 'true' || value === 'yes' || value === 'Yes';
                                  const box = checked ? '&#9745;' : '&#9744;';
                                  return '<p style="margin: 6px 0;">' + box + ' <strong>' + label + '</strong></p>';
                                }
                                
                                // Checkbox group - show all options with check state
                                if (ftype === 'checkbox_group' || ftype === 'multiselect') {
                                  const selected = Array.isArray(value) ? value : (value ? [value] : []);
                                  let html = '<div style="margin: 8px 0;"><strong>' + label + ':</strong><div style="margin-top: 4px; padding-left: 8px;">';
                                  if (options.length > 0) {
                                    options.forEach(opt => {
                                      const optLabel = typeof opt === 'object' ? (opt.label || opt.value || opt) : opt;
                                      const isChecked = selected.includes(optLabel);
                                      const box = isChecked ? '&#9745;' : '&#9744;';
                                      html += '<div style="margin: 2px 0;">' + box + ' ' + optLabel + '</div>';
                                    });
                                  } else if (selected.length > 0) {
                                    selected.forEach(s => { html += '<div style="margin: 2px 0;">&#9745; ' + s + '</div>'; });
                                  } else {
                                    html += '<span style="color: #9ca3af; font-style: italic;">(none selected)</span>';
                                  }
                                  html += '</div></div>';
                                  return html;
                                }
                                
                                // Radio buttons
                                if (ftype === 'radio' || ftype === 'select' || ftype === 'dropdown') {
                                  const selected = Array.isArray(value) ? value : (value ? [value] : []);
                                  if (options.length > 0) {
                                    let html = '<div style="margin: 8px 0;"><strong>' + label + ':</strong><div style="margin-top: 4px; padding-left: 8px;">';
                                    options.forEach(opt => {
                                      const optLabel = typeof opt === 'object' ? (opt.label || opt.value || opt) : opt;
                                      const isSelected = selected.includes(optLabel);
                                      const dot = isSelected ? '&#9679;' : '&#9675;';
                                      html += '<div style="margin: 2px 0;">' + dot + ' ' + optLabel + '</div>';
                                    });
                                    html += '</div></div>';
                                    return html;
                                  }
                                }
                                
                                // Date field
                                if (ftype === 'date') {
                                  const displayValue = value ? new Date(value).toLocaleDateString() : '';
                                  const isEmpty = !displayValue;
                                  return '<p style="margin: 6px 0;"><strong>' + label + ':</strong> <span style="' + (isEmpty ? 'color: #9ca3af; font-style: italic;' : '') + '">' + (isEmpty ? '(not filled)' : displayValue) + '</span></p>';
                                }
                                
                                // Default: textarea, text, consent, treatment_plan_goals, etc.
                                const displayValue = Array.isArray(value) ? value.join(', ') : String(value || '');
                                const isEmpty = !displayValue.trim();
                                const shown = isEmpty ? '(not filled)' : displayValue;
                                
                                if (!isEmpty && shown.includes('<') && shown.includes('>')) {
                                  return '<div style="margin: 10px 0;"><strong>' + label + ':</strong><div style="margin-top: 4px;">' + shown + '</div></div>';
                                }
                                return '<p style="margin: 6px 0;"><strong>' + label + ':</strong> <span style="' + (isEmpty ? 'color: #9ca3af; font-style: italic;' : '') + '">' + shown + '</span></p>';
                              };
                              
                              // Handle top-level fields format (most common)
                              if (noteData.fields && Array.isArray(noteData.fields)) {
                                noteContentFS = noteData.fields.map(renderField).join('');
                              }
                              // Handle sections format  
                              else if (noteData.sections) {
                                noteContentFS = noteData.sections.map(section => {
                                  let sectionHtml = '<h3>' + (section.title || 'Section') + '</h3>';
                                  (section.fields || []).forEach(field => { sectionHtml += renderField(field); });
                                  return sectionHtml;
                                }).join('');
                              }
                              // Handle fields as object/dict
                              else if (noteData.fields && typeof noteData.fields === 'object') {
                                noteContentFS = Object.entries(noteData.fields).map(([key, value]) => {
                                  const displayValue = Array.isArray(value) ? value.join(', ') : String(value || '');
                                  const isEmpty = !displayValue.trim();
                                  return '<p style="margin: 6px 0;"><strong>' + key + ':</strong> <span style="' + (isEmpty ? 'color: #9ca3af; font-style: italic;' : '') + '">' + (isEmpty ? '(not filled)' : displayValue) + '</span></p>';
                                }).join('');
                              }
                            } catch (e) {
                              noteContentFS = '<pre style="white-space: pre-wrap; font-family: inherit;">' + (selectedNote.content || '') + '</pre>';
                            }
                            
                            // Build signatures HTML
                            let signaturesFS = '';
                            const sigStyle = 'border-top: 2px solid #e5e7eb; margin-top: 24px; padding-top: 16px;';
                            const sigImgStyle = 'max-height: 80px; max-width: 300px; display: block; margin: 8px 0;';
                            const sigLabelStyle = 'font-size: 13px; color: #6b7280; margin: 0;';
                            const sigNameStyle = 'font-size: 14px; font-weight: 600; color: #374151; margin: 4px 0 0 0;';
                            
                            // Provider signature
                            if (selectedNote.provider_signed_at) {
                              const providerSigner = users.find(u => u.id === selectedNote.provider_signed_by);
                              signaturesFS += '<div style="' + sigStyle + '">';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Provider Signature</p>';
                              if (selectedNote.provider_signature && selectedNote.provider_signature.startsWith('data:image')) {
                                signaturesFS += '<img src="' + selectedNote.provider_signature + '" style="' + sigImgStyle + '" />';
                              }
                              signaturesFS += '<p style="' + sigNameStyle + '">' + (providerSigner?.full_name || 'Unknown') + (providerSigner?.credentials ? ', ' + providerSigner.credentials : '') + '</p>';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Signed: ' + new Date(selectedNote.provider_signed_at).toLocaleString() + '</p>';
                              signaturesFS += '</div>';
                            }
                            
                            // Supervisor signature
                            if (selectedNote.supervisor_signed_at) {
                              const supSigner = users.find(u => u.id === selectedNote.supervisor_signed_by);
                              signaturesFS += '<div style="' + sigStyle + '">';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Supervisor Signature</p>';
                              if (selectedNote.supervisor_signature && selectedNote.supervisor_signature.startsWith('data:image')) {
                                signaturesFS += '<img src="' + selectedNote.supervisor_signature + '" style="' + sigImgStyle + '" />';
                              }
                              signaturesFS += '<p style="' + sigNameStyle + '">' + (supSigner?.full_name || 'Unknown') + (supSigner?.credentials ? ', ' + supSigner.credentials : '') + '</p>';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Signed: ' + new Date(selectedNote.supervisor_signed_at).toLocaleString() + '</p>';
                              signaturesFS += '</div>';
                            }
                            
                            // Supervisor 2 signature
                            if (selectedNote.supervisor_2_signed_at) {
                              const sup2Signer = users.find(u => u.id === selectedNote.supervisor_2_signed_by);
                              signaturesFS += '<div style="' + sigStyle + '">';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Supervisor Signature (2)</p>';
                              if (selectedNote.supervisor_2_signature && selectedNote.supervisor_2_signature.startsWith('data:image')) {
                                signaturesFS += '<img src="' + selectedNote.supervisor_2_signature + '" style="' + sigImgStyle + '" />';
                              }
                              signaturesFS += '<p style="' + sigNameStyle + '">' + (sup2Signer?.full_name || 'Unknown') + (sup2Signer?.credentials ? ', ' + sup2Signer.credentials : '') + '</p>';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Signed: ' + new Date(selectedNote.supervisor_2_signed_at).toLocaleString() + '</p>';
                              signaturesFS += '</div>';
                            }
                            
                            // Client signature
                            if (selectedNote.client_signed_at) {
                              signaturesFS += '<div style="' + sigStyle + '">';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Client Signature</p>';
                              if (selectedNote.client_signature && selectedNote.client_signature.startsWith('data:image')) {
                                signaturesFS += '<img src="' + selectedNote.client_signature + '" style="' + sigImgStyle + '" />';
                              }
                              signaturesFS += '<p style="' + sigNameStyle + '">' + (selectedNote.client_signature_name || noteClientFS?.full_name || 'Client') + '</p>';
                              signaturesFS += '<p style="' + sigLabelStyle + '">Signed: ' + new Date(selectedNote.client_signed_at).toLocaleString() + '</p>';
                              signaturesFS += '</div>';
                            }
                            
                            // Additional signatures
                            if (selectedNote.additional_signatures && selectedNote.additional_signatures.length > 0) {
                              selectedNote.additional_signatures.forEach(addSig => {
                                signaturesFS += '<div style="' + sigStyle + '">';
                                signaturesFS += '<p style="' + sigLabelStyle + '">Additional Signature (' + (addSig.sign_as || 'staff') + ')</p>';
                                // Additional signatures may use GCS URLs — skip image if not base64
                                if (addSig.signature_image && addSig.signature_image.startsWith('data:image')) {
                                  signaturesFS += '<img src="' + addSig.signature_image + '" style="' + sigImgStyle + '" />';
                                }
                                signaturesFS += '<p style="' + sigNameStyle + '">' + (addSig.signer_name || 'Unknown') + '</p>';
                                signaturesFS += '<p style="' + sigLabelStyle + '">Signed: ' + new Date(addSig.signed_at).toLocaleString() + '</p>';
                                signaturesFS += '</div>';
                              });
                            }
                            
                            const htmlContent = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>' + (templateNameFS + ' - ' + (noteClientFS?.full_name || 'Patient')).replace(/</g, '&lt;') + '</title>' +
                              '<style>' +
                              'body { font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; padding: 0; margin: 0; line-height: 1.6; background: #f8fafc; color: #374151; font-size: 14px; }' +
                              '.header-bar { background: #f0fdfa; padding: 15px 30px; border-bottom: 2px solid #0d9488; }' +
                              '.header-row { margin-bottom: 8px; }' +
                              '.note-title { font-size: 18px; font-weight: 600; color: #0f766e; }' +
                              '.info-row { font-size: 13px; color: #4b5563; padding: 6px 0; border-top: 1px solid #e5e7eb; }' +
                              '.info-row strong { color: #0f766e; }' +
                              '.content-area { padding: 24px 30px; max-width: 900px; margin: 0 auto; background: white; min-height: calc(100vh - 180px); }' +
                              'h3 { color: #0f766e; font-size: 15px; font-weight: 600; margin: 20px 0 8px 0; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }' +
                              'p { margin: 6px 0; font-size: 14px; }' +
                              'strong { color: #374151; font-weight: 600; }' +
                              '@media print { body { background: white; } .header-bar { background: #f0fdfa; } .content-area { padding: 20px; } }' +
                              '</style></head><body>' +
                              '<div class="header-bar">' +
                              '<div class="header-row"><span class="note-title">' + templateNameFS + '</span></div>' +
                              '<div class="info-row"><strong>Client:</strong> ' + (noteClientFS?.full_name || 'N/A') + ' (MRN: ' + (noteClientFS?.mrn?.replace('MRN-', '') || 'N/A') + ') | ' +
                              '<strong>DOB:</strong> ' + (noteClientFS?.date_of_birth ? fmtLocalDate(noteClientFS.date_of_birth) : 'N/A') +
                              (primaryDxFS ? ' | <strong>Dx:</strong> ' + primaryDxFS : '') + '</div>' +
                              '<div class="info-row"><strong>Provider:</strong> ' + (noteProviderFS?.full_name || 'N/A') + (noteProviderFS?.credentials ? ', ' + noteProviderFS.credentials : '') + ' | ' +
                              '<strong>Appointment:</strong> ' + appointmentInfoFS + '</div>' +
                              '<div class="info-row"><strong>Date of Service:</strong> ' + (selectedNote.date_of_service ? fmtLocalDate(selectedNote.date_of_service) : 'N/A') + ' | ' +
                              '<strong>Created:</strong> ' + (selectedNote.created_at ? new Date(selectedNote.created_at).toLocaleString() : 'N/A') + '</div>' +
                              '</div><div class="content-area">' + noteContentFS + signaturesFS + '</div></body></html>';
                            
                            const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
                            const blobUrl = URL.createObjectURL(blob);
                            window.open(blobUrl, '_blank');
                        }}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Full Screen
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {/* Lock/Unlock Note */}
                        {!selectedNote.is_locked && canLockNote(selectedNote) && (
                          <DropdownMenuItem onClick={async () => {
                            const noteId = selectedNote.id;
                            const currentStatus = selectedNote.status;
                            updateNoteStatusOptimistically(noteId, currentStatus, { is_locked: true, locked_at: new Date().toISOString(), locked_by: user.id });
                            try {
                              await axios.put(`${API}/notes/${noteId}`, {
                                is_locked: true,
                                locked_at: new Date().toISOString(),
                                locked_by: user.id
                              });
                              toast.success('Note locked');
                              refreshNotes(); refreshTodos();
                              setShowNoteDetail(false);
                            } catch (error) {
                              toast.error('Failed to lock note');
                              refreshNotes(); refreshTodos();
                              refreshClientNotes();
                            }
                          }}>
                            <Lock className="h-4 w-4 mr-2" />
                            Lock Note
                          </DropdownMenuItem>
                        )}
                        {selectedNote.is_locked && canLockNote(selectedNote) && (
                          <DropdownMenuItem onClick={async () => {
                            const noteId = selectedNote.id;
                            const currentStatus = selectedNote.status;
                            updateNoteStatusOptimistically(noteId, currentStatus, { is_locked: false, unlocked_at: new Date().toISOString(), unlocked_by: user.id });
                            try {
                              await axios.put(`${API}/notes/${noteId}`, {
                                is_locked: false,
                                unlocked_at: new Date().toISOString(),
                                unlocked_by: user.id
                              });
                              toast.success('Note unlocked');
                              refreshNotes(); refreshTodos();
                              setShowNoteDetail(false);
                            } catch (error) {
                              toast.error('Failed to unlock note');
                              refreshNotes(); refreshTodos();
                              refreshClientNotes();
                            }
                          }}>
                            <Unlock className="h-4 w-4 mr-2" />
                            Unlock Note
                          </DropdownMenuItem>
                        )}
                        
                        {/* Request Corrections */}
                        {selectedNote.status !== 'needs_corrections' && (
                          <DropdownMenuItem onClick={() => {
                            setCorrectionsReason('');
                            setShowCorrectionsDialog(true);
                          }}>
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            Request Corrections
                          </DropdownMenuItem>
                        )}
                        
                        {/* View History */}
                        <DropdownMenuItem 
                          onClick={() => setShowNoteHistory(true)}
                          data-testid="view-note-history-btn"
                        >
                          <History className="h-4 w-4 mr-2" />
                          View History
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {/* Download PDF */}
                        {(() => {
                          const requiresSignature = noteTemplate?.download_requires_signature !== false;
                          const isSignedOrLocked = selectedNote.status === 'signed_ready_for_billing' || selectedNote.status === 'completed' || selectedNote.status === 'locked' || selectedNote.is_locked;
                          const canDownload = noteTemplate?.allow_external_download && (!requiresSignature || isSignedOrLocked);
                          const hasDivider = noteHasClientVisibilityDivider(selectedNote.content);
                          
                          if (canDownload) {
                            return (
                              <>
                                <DropdownMenuItem onClick={() => {
                                  const token = sessionStorage.getItem('token');
                                  window.open(`${API}/notes/${selectedNote.id}/wysiwyg-pdf?token=${token}&mode=full`, '_blank');
                                }}>
                                  <Download className="h-4 w-4 mr-2" />
                                  {hasDivider ? 'Download Full PDF' : 'Download PDF'}
                                </DropdownMenuItem>
                                {hasDivider && (
                                  <DropdownMenuItem onClick={() => {
                                    const token = sessionStorage.getItem('token');
                                    window.open(`${API}/notes/${selectedNote.id}/wysiwyg-pdf?token=${token}&mode=summary`, '_blank');
                                  }}>
                                    <FileText className="h-4 w-4 mr-2" />
                                    Download Client Summary
                                  </DropdownMenuItem>
                                )}
                              </>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Print for Signature — Feb 2026: now universal.
                            Previously gated by `noteTemplate?.allow_print_for_signature`
                            which hid it on most forms.  Mirrors the Treatment
                            Plan kebab fix: any user (provider, office, admin)
                            should always have a way to print a note and collect
                            a wet signature in office, regardless of template
                            settings.  The endpoint itself just renders the note
                            content; no PHI/admin gate exists beyond auth. */}
                        <DropdownMenuItem onClick={() => {
                          const token = sessionStorage.getItem('token');
                          window.open(`${API}/notes/${selectedNote.id}/print-for-signature?token=${token}`, '_blank');
                        }}
                        data-testid="print-for-signature-btn"
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          Print for Signature
                        </DropdownMenuItem>
                        
                        {/* Send to Client */}
                        {noteTemplate?.allow_send_to_client && selectedNote.status !== 'sent_to_client_for_signature' && (
                          <DropdownMenuItem onClick={() => {
                            setSendToClientNote(selectedNote);
                            setShowSendToClientDialog(true);
                          }}>
                            <Send className="h-4 w-4 mr-2" />
                            {(selectedNote.client_signature || selectedNote.client_signed_at) ? 'Send Copy to Client' : 'Send to Client'}
                          </DropdownMenuItem>
                        )}
                        
                        {/* Recall from Client - only show when sent to client */}
                        {selectedNote.status === 'sent_to_client_for_signature' && (
                          <DropdownMenuItem 
                            onClick={async () => {
                              if (!window.confirm('Are you sure you want to recall this form from the client? They will no longer be able to access or sign it.')) {
                                return;
                              }
                              try {
                                await axios.put(`${API}/notes/${selectedNote.id}`, {
                                  status: 'draft',
                                  recalled_at: new Date().toISOString(),
                                  recalled_by: user.id
                                });
                                toast.success('Form recalled from client');
                                refreshNotes(); refreshTodos();
                              } catch (error) {
                                console.error('Error recalling from client:', error);
                                toast.error(error.response?.data?.detail || 'Failed to recall from client');
                              }
                            }}
                            className="text-orange-600"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Recall from Client
                          </DropdownMenuItem>
                        )}

                        {/* Mar 2026 — Mark Non-Billable escape hatch
                            on the three-dots menu.  Available for any
                            note that isn't already billed / paid /
                            archived and isn't already non-billable.
                            Lets billers (and signers) mark a note as
                            duplicate or CPT-restricted from anywhere
                            the note can be opened. */}
                        {selectedNote &&
                          !['billed', 'paid', 'not_billable', 'archived', 'closed'].includes(selectedNote.status) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid="note-three-dot-mark-not-billable"
                              className="text-slate-700"
                              onClick={() => {
                                setNotBillableTargetNote(selectedNote);
                                setNotBillableReason('');
                                setNotBillableComment('');
                                setShowNotBillableDialog(true);
                              }}
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Mark Non-Billable / Duplicate
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {/* Cancel Button */}
                    {isEditingNote && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // EXIT GUARD: Use hasRealUnsavedChanges to check for meaningful differences
                          // This prevents false "unsaved changes" warnings when content matches server
                          // CRITICAL: Fall back to false to prevent false positives
                          const hasRealChanges = noteEditingAutosave.hasRealUnsavedChanges?.() ?? false;
                          if (!hasRealChanges || window.confirm('Discard unsaved changes?')) {
                            noteEditingAutosave.clearLocalDraft?.();
                            setIsEditingNote(false);
                            setShowNoteDetail(false);
                            setSelectedNote(null);
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    
                    {/* Save Changes Button */}
                    {/* Save Changes Button - STOPLIGHT: Turns RED when blocked by autosave error */}
                    {isEditingNote && (
                      <Button
                        size="sm"
                        disabled={isSavingNote || (!!autosaveBlockedReason && !isContentLossBlock)}
                        title={autosaveBlockedReason || undefined}
                        onClick={async () => {
                          if (isSavingNote) return;
                          if (autosaveBlockedReason) {
                            if (isContentLossBlock) setShowLossOverride(true);
                            return;
                          }
                          setIsSavingNote(true);
                          try {
                            const updateData = {
                              content: JSON.stringify(editedNoteContent),
                              date_of_service: selectedNote.date_of_service,
                              start_time: selectedNote.start_time,
                              end_time: selectedNote.end_time,
                              units: selectedNote.units,
                              cpt_code: selectedNote.cpt_code,
                              cpt_code_id: selectedNote.cpt_code_id,
                              // Persist the multi-select add-on list — server's
                              // update_note triggers note→appointment sync.
                              addon_cpt_code_ids: Array.isArray(selectedNote.addon_cpt_code_ids)
                                ? selectedNote.addon_cpt_code_ids
                                : [],
                              appointment_id: selectedNote.appointment_id,
                              linked_goals: linkedGoals,
                              // Echo guard: only send consent when the editor actually changed it
                              // vs the load snapshot, so a manual save can't overwrite modality
                              // fixes made elsewhere (e.g. biller set 93) since the note was opened.
                              ...(JSON.stringify(editedNoteConsent || null) !== JSON.stringify(selectedNote.telehealth_consent || null)
                                ? { telehealth_consent: editedNoteConsent }
                                : {}),
                              updated_at: new Date().toISOString()
                            };
                            const savedNoteId = await saveNoteContentWithRecovery(updateData);
                            toast.success('Note saved successfully');
                            // Clear error states on successful save
                            setNoteCreationError(null);
                            setAutosaveBlockedReason(null);
                            setIsEditingNote(false);
                            setEditedNoteConsent(null);
                            noteEditingAutosave.clearLocalDraft?.();
                            refreshNotes(); refreshTodos();
                            const updatedNote = await axios.get(`${API}/notes/${savedNoteId}`);
                            setSelectedNote(updatedNote.data);
                            // Jun 2026 stale-autosave fix: the PUT bumped the
                            // note's current_version server-side — sync the
                            // autosave hook so (a) our own future autosaves
                            // don't hit a spurious conflict and (b) any STALE
                            // autosave elsewhere now fails its version check
                            // instead of silently reverting this manual save.
                            if (updatedNote.data?.current_version) {
                              noteEditingAutosave.setVersion?.(updatedNote.data.current_version);
                            }
                          } catch (error) {
                            console.error('Save error:', error);
                            // LOUD FAILURE: Set error state to show banner
                            setNoteCreationError(`Failed to save: ${error.response?.data?.detail || error.message}`);
                            setAutosaveBlockedReason('Save failed - see error above');
                            toast.error(`Failed to update note: ${error.response?.data?.detail || error.message}`);
                          } finally {
                            setIsSavingNote(false);
                          }
                        }}
                        className={autosaveBlockedReason
                          ? (isContentLossBlock
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-red-600 hover:bg-red-700 cursor-not-allowed opacity-75')
                          : 'bg-teal-600 hover:bg-teal-700'}
                        data-testid="save-changes-button"
                      >
                        {isSavingNote ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Saving...
                          </>
                        ) : autosaveBlockedReason ? (
                          <>
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            {isContentLossBlock ? 'Save Blocked — Review' : 'Save Blocked'}
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    )}
                    
                    {/* Edit Button (when not editing) */}
                    {!isEditingNote && canEditNote(selectedNote) && (
                      <Button
                        size="sm"
                        data-testid="edit-note-button"
                        onClick={async () => {
                          // Jun 2026 DATA-PRESERVATION (user report: manually
                          // saving someone else's note blanked it): the
                          // fullscreen /note/:id route bypasses openNote, so
                          // editedNoteContent is uninitialized here — entering
                          // edit rendered a BLANK form whose "Save Changes"
                          // wiped the note. Run the full openNote init (bundle
                          // fetch + template merge) before entering edit.
                          if (!editedNoteContent) {
                            await openNote(selectedNote);
                            // openNote auto-enters edit for drafts; for signed
                            // notes flip to edit now that content is loaded.
                            if ((selectedNote.status || '') !== 'draft') {
                              setIsEditingNote(true);
                            }
                            return;
                          }
                          setIsEditingNote(true);
                          // Initialize consent data when entering edit mode
                          if (selectedNote.telehealth_consent?.pos_code) {
                            setEditedNoteConsent(selectedNote.telehealth_consent);
                          } else if (selectedNote.appointment_id) {
                            const linkedAppointment = appointments.find(apt => apt.id === selectedNote.appointment_id) || noteLinkedAppointment;
                            if (linkedAppointment?.pos_code) {
                              const effectivePosCodes = getEffectivePosCodes(organizations, posCodes);
                              const posObj = effectivePosCodes.find(p => p.code === linkedAppointment.pos_code);
                              setEditedNoteConsent({
                                provider_location: linkedAppointment.location_id || null,
                                pos_code: linkedAppointment.pos_code,
                                pos_description: posObj?.description || '',
                                modality: linkedAppointment.telehealth_modality || (linkedAppointment.google_meet_link ? 'audio_visual' : 'in_person')
                              });
                            }
                          }
                        }}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
                </div>
                
                {/* Condensed Client Info Row - with Provider on the right */}
                <div className="px-4 py-2 border-b bg-gray-50 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-700 flex-wrap">
                      <span className="font-medium">{noteClient?.full_name || 'Unknown Client'}</span>
                      {noteClient?.mrn && (
                        <>
                          <span className="text-gray-400">(MRN: #{noteClient.mrn.replace('MRN-', '')})</span>
                        </>
                      )}
                      {noteClient?.date_of_birth && (
                        <>
                          <span className="text-gray-400">|</span>
                          <span>{formatDateMMDDYYYY(noteClient.date_of_birth)} ({calculateAge(noteClient.date_of_birth)}y)</span>
                        </>
                      )}
                      {/* Primary Diagnosis - Use snapshot if available, else fallback to live lookup */}
                      {(() => {
                        // First check if note has a diagnosis snapshot (frozen at creation)
                        if (selectedNote.diagnosis_snapshot?.primary) {
                          const snapshotDx = selectedNote.diagnosis_snapshot.primary;
                          return (
                            <>
                              <span className="text-gray-400">|</span>
                              <span className="text-teal-700 font-medium flex items-center gap-1">
                                Dx: {snapshotDx.code} - {snapshotDx.description}
                                {snapshotDx.diagnosis_tag && (
                                  <span className={`px-1 py-0.5 rounded text-[10px] ${
                                    snapshotDx.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                                    snapshotDx.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                                    snapshotDx.diagnosis_tag === 'rule_out' ? 'bg-red-100 text-red-700' :
                                    snapshotDx.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {snapshotDx.diagnosis_tag === 'differential' ? 'Diff' : snapshotDx.diagnosis_tag === 'working' ? 'Wrk' : snapshotDx.diagnosis_tag === 'rule_out' ? 'R/O' : snapshotDx.diagnosis_tag === 'provisional' ? 'Prov' : snapshotDx.diagnosis_tag}
                                  </span>
                                )}
                              </span>
                            </>
                          );
                        }
                        // Fallback: live lookup from client diagnoses (for legacy notes)
                        if (noteClient?.id) {
                          const primaryDiagnosis = clientDiagnoses.find(d => 
                            d.client_id === noteClient.id && d.is_primary && d.is_active
                          );
                          if (primaryDiagnosis) {
                            return (
                              <>
                                <span className="text-gray-400">|</span>
                                <span className="text-teal-700 font-medium flex items-center gap-1">
                                  Dx: {primaryDiagnosis.diagnosis_code} - {primaryDiagnosis.diagnosis_description}
                                  {primaryDiagnosis.diagnosis_tag && (
                                    <span className={`px-1 py-0.5 rounded text-[10px] ${
                                      primaryDiagnosis.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                                      primaryDiagnosis.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                                      primaryDiagnosis.diagnosis_tag === 'rule_out' ? 'bg-red-100 text-red-700' :
                                      primaryDiagnosis.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {primaryDiagnosis.diagnosis_tag === 'differential' ? 'Diff' : primaryDiagnosis.diagnosis_tag === 'working' ? 'Wrk' : primaryDiagnosis.diagnosis_tag === 'rule_out' ? 'R/O' : primaryDiagnosis.diagnosis_tag === 'provisional' ? 'Prov' : primaryDiagnosis.diagnosis_tag}
                                    </span>
                                  )}
                                </span>
                              </>
                            );
                          }
                        }
                        return null;
                      })()}
                    </div>
                    
                    {/* Right side: Provider + Lock + Duplicate */}
                    <div className="flex items-center gap-3">
                      {/* Provider with credentials */}
                      <div className="flex items-center gap-1 text-gray-600">
                        <span className="text-gray-400">
                          {selectedNote.form_type === 'medical_records_release' || 
                           selectedNote.note_type === 'medical_records_release' ||
                           templateName?.toLowerCase().includes('medical records') 
                            ? 'By:' 
                            : 'Provider:'}
                        </span>
                        <span className="font-medium text-gray-700">
                          {noteProvider?.full_name
                            || (selectedNote.provider_id ? 'Former provider' : 'Not assigned')}
                          {noteProvider?.credentials && <span className="text-gray-500">, {noteProvider.credentials}</span>}
                          {noteProvider?.is_archived && <span className="text-gray-400 text-xs ml-1">(archived)</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Appointment & Service Details - Single Row */}
                <div className="px-4 py-2 border-b bg-gray-50 text-sm">
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* Linked Appointment */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Appt:</span>
                      {isEditingNote ? (
                        <AppointmentLinkSelect
                          value={selectedNote.appointment_id || 'none'}
                          appointments={appointments}
                          clientGroups={clientGroups}
                          API={API}
                          fallbackAppointment={noteLinkedAppointment}
                          clientIds={
                            selectedNote.is_group_note && selectedNote.client_ids
                              ? selectedNote.client_ids
                              : (selectedNote.client_id ? [selectedNote.client_id] : [])
                          }
                          groupAware={true}
                          triggerClassName="h-7 w-[350px] text-xs"
                          placeholder="Select appointment..."
                          noneLabel="No appointment linked"
                          testIdPrefix="appt-link"
                          renderLabel={(apt) => {
                            const aptDate = new Date(apt.start_time).toLocaleDateString();
                            const aptTime = new Date(apt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const aptClient = allClientsForLookup.find(c => c.id === apt.client_id);
                            const aptGroup = (apt.selected_group_id || apt.group_id)
                              ? clientGroups.find(g => g.id === (apt.selected_group_id || apt.group_id))
                              : null;
                            const aptCPT = cptCodes.find(c => c.id === apt.cpt_code_id);
                            const linkedNotesCount = notes.filter(n => n.appointment_id === apt.id && n.id !== selectedNote.id).length;
                            const suffix = linkedNotesCount > 0
                              ? ` (${linkedNotesCount} note${linkedNotesCount > 1 ? 's' : ''} linked)`
                              : '';
                            return `${aptDate} ${aptTime} - ${aptGroup ? `📋 ${aptGroup.name}` : aptClient?.full_name} - ${aptCPT?.code || 'No CPT'}${suffix}`;
                          }}
                          onChange={async (newAppointmentId, apt) => {
                            try {
                              const updateData = { appointment_id: newAppointmentId };
                              if (newAppointmentId && apt) {
                                // Clinic-local (Eastern) calendar date, not UTC — an evening
                                // appointment stored as next-day UTC must not shift the DOS.
                                updateData.date_of_service = new Date(apt.start_time)
                                  .toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
                              }
                              await axios.put(`${API}/notes/${selectedNote.id}`, updateData);
                              setSelectedNote({...selectedNote, ...updateData});
                              toast.success(newAppointmentId ? 'Appointment linked successfully' : 'Appointment link removed');
                              refreshNotes(); refreshTodos();
                            } catch (error) {
                              toast.error('Failed to update appointment link');
                            }
                          }}
                        />
                      ) : (
                        (() => {
                          if (selectedNote.appointment_id) {
                            const linkedAppointment = appointments.find(a => a.id === selectedNote.appointment_id) || noteLinkedAppointment;
                            if (linkedAppointment) {
                              const aptDate = new Date(linkedAppointment.start_time).toLocaleDateString();
                              const aptTime = new Date(linkedAppointment.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              const aptCPT = cptCodes.find(c => c.id === linkedAppointment.cpt_code_id);
                              const aptLocation = linkedAppointment.location_id ? locations.find(l => l.id === linkedAppointment.location_id) : null;
                              // Get POS info using the getEffectivePosCodes helper
                              const posCode = linkedAppointment.pos_code;
                              const effectivePosCodes = getEffectivePosCodes(organizations, posCodes);
                              const posInfo = posCode ? effectivePosCodes.find(p => p.code === posCode) : null;
                              const posDisplay = posInfo ? `${posInfo.code} - ${posInfo.name || posInfo.description}` : (posCode || null);
                              // Determine modality
                              const isTelehealth = posCode === '02' || posCode === '10' || linkedAppointment.google_meet_link;
                              const telehealthType = linkedAppointment.telehealth_modality || (linkedAppointment.google_meet_link ? 'audio_visual' : null);
                              
                              return (
                                <span className="text-teal-700">
                                  ✓ {aptDate} {aptTime}
                                  {aptCPT && <span> | {aptCPT.code} - {aptCPT.name || aptCPT.description}</span>}
                                  {posDisplay && <span className="text-gray-600"> | POS: {posDisplay}</span>}
                                  {aptLocation && <span className="text-gray-600"> | {aptLocation.name}</span>}
                                  {isTelehealth && (
                                    <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                      {telehealthType === 'audio_visual' ? '📹 Telehealth' : 
                                       telehealthType === 'audio_only' ? '📞 Phone' : '🌐 Telehealth'}
                                    </span>
                                  )}
                                </span>
                              );
                            }
                            return <span className="text-red-600">Appointment not found</span>;
                          }
                          return <span className="text-gray-400">None linked</span>;
                        })()
                      )}
                    </div>
                  
                    {/* DOS, Start/End, Units, CPT - inline on same row */}
                    <span className="text-gray-300">|</span>
                    {selectedNote.appointment_id ? (
                      // Appointment is linked - show times from appointment (read-only)
                      (() => {
                        const linkedAppointment = appointments.find(a => a.id === selectedNote.appointment_id) || noteLinkedAppointment;
                        if (linkedAppointment) {
                          const aptDate = new Date(linkedAppointment.start_time).toLocaleDateString();
                          const startTime = new Date(linkedAppointment.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                          const endTime = new Date(linkedAppointment.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                          const durationMs = new Date(linkedAppointment.end_time) - new Date(linkedAppointment.start_time);
                          const durationMin = Math.round(durationMs / 60000);
                          const units = linkedAppointment.units || 1;
                          
                          return (
                            <>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">DOS:</span>
                                <span>{aptDate}</span>
                                <span className="text-xs text-gray-400">(from appt)</span>
                              </div>
                              <span className="text-gray-300">|</span>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">Start:</span>
                                <span>{startTime}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">End:</span>
                                <span>{endTime}</span>
                              </div>
                              <span className="text-gray-500">({durationMin} min, {units} {units === 1 ? 'unit' : 'units'})</span>
                              {/* Jun 2026 — CPT visibility for linked notes. If the linked
                                  appointment resolves a CPT, show it read-only; otherwise let
                                  the provider pick the note's own CPT here (billing falls back
                                  to note.cpt_code) instead of forcing unlink → pick → relink. */}
                              {(() => {
                                const aptCptResolved = cptCodes.find(c => c.id === linkedAppointment.cpt_code_id)
                                  || (() => {
                                    const m = (linkedAppointment.service_type || '').trim().match(/^([A-Za-z]\d{4}|\d{4,5}[A-Za-z]?)/);
                                    return m ? cptCodes.find(c => String(c.code).toUpperCase() === m[1].toUpperCase()) : null;
                                  })();
                                if (aptCptResolved) {
                                  return (
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-gray-300">|</span>
                                      <span className="text-gray-500">CPT:</span>
                                      <span data-testid="note-linked-appt-cpt">{aptCptResolved.code}</span>
                                      <span className="text-xs text-gray-400">(from appt)</span>
                                    </div>
                                  );
                                }
                                const noteCpt = cptCodes.find(c => c.id === selectedNote.cpt_code_id)
                                  || cptCodes.find(c => c.code === selectedNote.cpt_code);
                                if (!isEditingNote) {
                                  return (
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-gray-300">|</span>
                                      <span className="text-gray-500">CPT:</span>
                                      {noteCpt || selectedNote.cpt_code
                                        ? <span data-testid="note-linked-note-cpt">{noteCpt?.code || selectedNote.cpt_code}</span>
                                        : <span className="text-amber-600 text-xs font-medium" data-testid="note-linked-no-cpt">Not set — edit note to choose</span>}
                                    </div>
                                  );
                                }
                                const accessible = getAccessibleCPTCodes(selectedNote.provider_id);
                                return (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-300">|</span>
                                    <span className="text-amber-600 font-medium">CPT:</span>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button variant="outline" role="combobox"
                                          className="h-7 w-56 justify-between text-sm font-normal px-2"
                                          data-testid="note-linked-cpt-selector">
                                          <span className="truncate">
                                            {selectedNote.cpt_code
                                              ? `${selectedNote.cpt_code}${noteCpt?.description ? ` - ${noteCpt.description.substring(0, 24)}` : ''}`
                                              : 'Select CPT...'}
                                          </span>
                                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-[520px] p-0" align="start">
                                        <Command>
                                          <CommandInput placeholder="Search CPT code or description..." />
                                          <CommandList>
                                            <CommandEmpty>No CPT codes found.</CommandEmpty>
                                            <CommandGroup>
                                              {accessible.map(cpt => (
                                                <CommandItem
                                                  key={cpt.id}
                                                  value={`${cpt.code} ${cpt.description || ''}`}
                                                  onSelect={() => setSelectedNote(prev => ({ ...prev, cpt_code: cpt.code, cpt_code_id: cpt.id }))}
                                                  className="text-sm"
                                                >
                                                  <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${selectedNote.cpt_code_id === cpt.id ? 'opacity-100' : 'opacity-0'}`} />
                                                  <span className="font-semibold mr-2 flex-shrink-0">{cpt.code}</span>
                                                  <span className="text-gray-500">{cpt.description || ''}</span>
                                                </CommandItem>
                                              ))}
                                            </CommandGroup>
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                );
                              })()}
                              {/* Open/Unlink — moved here from the body's linked-appointment bar (user request) */}
                              <div className="ml-auto flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-blue-600 border-blue-300 hover:bg-blue-100"
                                  data-testid="note-header-open-appointment-btn"
                                  onClick={() => {
                                    // Find the CPT code by ID and format it correctly
                                    const aptCptCode = cptCodes.find(c => c.id === linkedAppointment.cpt_code_id);
                                    const formattedServiceType = aptCptCode 
                                      ? `${aptCptCode.display_name || aptCptCode.code} - ${aptCptCode.description}`
                                      : linkedAppointment.service_type;
                                    
                                    setAppointmentFormData({
                                      client_id: linkedAppointment.client_id || '',
                                      client_ids: linkedAppointment.client_ids || [],
                                      selected_group_id: linkedAppointment.selected_group_id || '',
                                      provider_id: linkedAppointment.provider_id,
                                      start_time: formatLocalDateTime(linkedAppointment.start_time),
                                      end_time: formatLocalDateTime(linkedAppointment.end_time),
                                      service_type: formattedServiceType,
                                      appointment_type: linkedAppointment.appointment_type || 'client_session',
                                      status: linkedAppointment.status || 'scheduled',
                                      is_billable: linkedAppointment.is_billable !== undefined ? linkedAppointment.is_billable : true,
                                      is_payroll_eligible: linkedAppointment.is_payroll_eligible || false,
                                      units: linkedAppointment.units || 1,
                                      location: linkedAppointment.location || '',
                                      notes: linkedAppointment.notes || '',
                                      cpt_code_id: linkedAppointment.cpt_code_id || '',
                                      selectedCptMeta: aptCptCode ? {
                                        allows_multiple_units: aptCptCode.allows_multiple_units,
                                        minutes_per_unit: aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60,
                                        code: aptCptCode.code || aptCptCode.display_name,
                                        is_group_session: aptCptCode.is_group_session || false
                                      } : null
                                    });
                                    
                                    // IMPORTANT: Set editingAppointment to indicate this is an UPDATE, not CREATE
                                    setEditingAppointment(linkedAppointment);
                                    setShowNoteDetail(false);
                                    setActiveTab('calendar');
                                    setShowAppointmentForm(true);
                                  }}
                                >
                                  Open Appointment
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-red-600 border-red-300 hover:bg-red-50"
                                  data-testid="note-header-unlink-appointment-btn"
                                  onClick={async () => {
                                    if (window.confirm('Unlink this note from its appointment?\n\nThe note will be preserved but will no longer be connected to the appointment.')) {
                                      try {
                                        await axios.put(`${API}/notes/${selectedNote.id}`, {
                                          ...selectedNote,
                                          appointment_id: null
                                        });
                                        setSelectedNote({...selectedNote, appointment_id: null});
                                        refreshNotes(); refreshTodos();
                                        toast.success('Appointment unlinked');
                                      } catch (error) {
                                        toast.error('Failed to unlink appointment');
                                      }
                                    }
                                  }}
                                >
                                  Unlink
                                </Button>
                              </div>
                            </>
                          );
                        }
                        return null;
                      })()
                    ) : (
                      // No appointment linked - show editable fields
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">DOS:</span>
                          {isEditingNote ? (
                            <Input
                              type="date"
                              value={selectedNote.date_of_service?.split('T')[0] || ''}
                              onChange={(e) => setSelectedNote({...selectedNote, date_of_service: e.target.value})}
                              onBlur={() => noteEditingAutosave.onFieldBlur?.('date_of_service')}
                              data-testid="note-header-dos-input"
                              className="h-7 w-40 text-sm"
                            />
                          ) : (
                            <span>{selectedNote.date_of_service ? formatDateMMDDYYYY(selectedNote.date_of_service) : 'Not set'}</span>
                          )}
                        </div>
                        <span className="text-gray-300">|</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Start:</span>
                          {isEditingNote ? (
                            <Input
                              type="time"
                              value={selectedNote.start_time || ''}
                              onChange={(e) => setSelectedNote({...selectedNote, start_time: e.target.value})}
                              onBlur={() => noteEditingAutosave.onFieldBlur?.('start_time')}
                              data-testid="note-header-start-input"
                              className="h-7 w-32 text-sm"
                            />
                          ) : (
                            <span>{selectedNote.start_time ? formatTimeTo12Hour(selectedNote.start_time) : 'Not set'}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">End:</span>
                          {isEditingNote ? (
                            <Input
                              type="time"
                              value={selectedNote.end_time || ''}
                              onChange={(e) => setSelectedNote({...selectedNote, end_time: e.target.value})}
                              onBlur={() => noteEditingAutosave.onFieldBlur?.('end_time')}
                              data-testid="note-header-end-input"
                              className="h-7 w-32 text-sm"
                            />
                          ) : (
                            <span>{selectedNote.end_time ? formatTimeTo12Hour(selectedNote.end_time) : 'Not set'}</span>
                          )}
                        </div>
                        <span className="text-gray-300">|</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Units:</span>
                          {isEditingNote ? (
                            <Input
                              type="number"
                              min="1"
                              value={selectedNote.units || 1}
                              onChange={(e) => {
                                const newUnits = parseInt(e.target.value) || 1;
                                // Recalc end_time from start + minutes_per_unit × units —
                                // same behavior as the CPT picker below and the appointment
                                // dialog, so changing units updates duration automatically.
                                setSelectedNote(prev => {
                                  const next = { ...prev, units: newUnits };
                                  const cpt = (cptCodes || []).find(c => c.id === prev.cpt_code_id)
                                    || (cptCodes || []).find(c => c.code === prev.cpt_code);
                                  const mins = cpt && (cpt.minutes_per_unit || cpt.duration_minutes);
                                  const start = prev.start_time;
                                  if (mins && start && typeof start === 'string' && /^\d{1,2}:\d{2}/.test(start) && !start.includes('T')) {
                                    const [h, m] = start.split(':').map(n => parseInt(n, 10));
                                    if (!Number.isNaN(h) && !Number.isNaN(m)) {
                                      const total = h * 60 + m + mins * newUnits;
                                      const endH = Math.floor((total / 60) % 24);
                                      const endM = total % 60;
                                      next.end_time = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                                    }
                                  }
                                  return next;
                                });
                              }}
                              className="h-7 w-20 text-sm"
                              data-testid="note-edit-units-input"
                            />
                          ) : (
                            <span>{selectedNote.units || 1}</span>
                          )}
                        </div>
                        <span className="text-gray-300">|</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">CPT:</span>
                          {isEditingNote ? (
                            (() => {
                              const accessibleCpts = getAccessibleCPTCodes(selectedNote.provider_id);
                              // Dedupe by composite identity (code +
                              // modifiers + description) so variants
                              // like "90791" base, "90791 +CANS",
                              // "90791 +HF" each appear separately.
                              // Bug pre-Feb 2026: dedupe was by `code`
                              // alone, so providers only saw the first
                              // variant and lost access to the rest.
                              const seen = new Set();
                              const dedupedCpts = accessibleCpts.filter(cpt => {
                                const mods = Array.isArray(cpt.modifiers)
                                  ? cpt.modifiers.join(',')
                                  : (cpt.modifiers || '');
                                const key = `${cpt.code}|${mods}|${(cpt.description || '').trim().toLowerCase()}`;
                                if (seen.has(key)) return false;
                                seen.add(key);
                                return true;
                              }).sort((a, b) => {
                                // Primary: numeric code; secondary:
                                // modifier string so variants of the
                                // same code group together.
                                const codeCmp = String(a.code).localeCompare(
                                  String(b.code), undefined, { numeric: true }
                                );
                                if (codeCmp !== 0) return codeCmp;
                                const am = Array.isArray(a.modifiers) ? a.modifiers.join(',') : (a.modifiers || '');
                                const bm = Array.isArray(b.modifiers) ? b.modifiers.join(',') : (b.modifiers || '');
                                return am.localeCompare(bm);
                              });
                              const selectedCptDesc = dedupedCpts.find(c => c.id === selectedNote.cpt_code_id) || dedupedCpts.find(c => c.code === selectedNote.cpt_code);
                              return (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      className="h-7 w-64 justify-between text-sm font-normal px-2"
                                      data-testid="note-edit-cpt-selector"
                                    >
                                      <span className="truncate">
                                        {selectedNote.cpt_code 
                                          ? `${selectedNote.cpt_code}${selectedCptDesc ? ` - ${selectedCptDesc.description?.substring(0, 28)}${selectedCptDesc.description?.length > 28 ? '...' : ''}` : ''}`
                                          : 'Select CPT...'}
                                      </span>
                                      <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[560px] p-0" align="start">
                                    <Command>
                                      <CommandInput placeholder="Search CPT code or description..." />
                                      <CommandList>
                                        <CommandEmpty>No CPT codes found.</CommandEmpty>
                                        <CommandGroup>
                                          {dedupedCpts.map(cpt => {
                                            const mods = Array.isArray(cpt.modifiers)
                                              ? cpt.modifiers.filter(Boolean).join('/')
                                              : (cpt.modifiers || '');
                                            return (
                                            <CommandItem
                                              key={cpt.id}
                                              value={`${cpt.code}${mods ? ` ${mods}` : ''} ${cpt.description || ''}`}
                                              onSelect={() => {
                                                // Auto-bump end_time to match the CPT's typical duration
                                                // so providers don't have to manually edit it after picking.
                                                // Only recalculate if we have a valid start_time; otherwise
                                                // just set the CPT and let the provider choose times.
                                                setSelectedNote(prev => {
                                                  const next = { ...prev, cpt_code: cpt.code, cpt_code_id: cpt.id };
                                                  const mins = cpt.minutes_per_unit || cpt.duration_minutes;
                                                  const start = prev.start_time;
                                                  if (mins && start && typeof start === 'string' && /^\d{1,2}:\d{2}/.test(start)) {
                                                    const [h, m] = start.split(':').map(n => parseInt(n, 10));
                                                    if (!Number.isNaN(h) && !Number.isNaN(m)) {
                                                      const units = parseInt(prev.units, 10) || 1;
                                                      const total = h * 60 + m + mins * units;
                                                      const endH = Math.floor((total / 60) % 24);
                                                      const endM = total % 60;
                                                      next.end_time = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                                                    }
                                                  }
                                                  return next;
                                                });
                                              }}
                                              className="text-sm"
                                            >
                                              <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${selectedNote.cpt_code_id === cpt.id ? 'opacity-100' : 'opacity-0'}`} />
                                              <span className="font-semibold mr-2 flex-shrink-0">{cpt.code}</span>
                                              {mods && (
                                                <span className="font-mono text-xs text-teal-700 mr-2 flex-shrink-0 bg-teal-50 px-1 rounded">
                                                  {mods}
                                                </span>
                                              )}
                                              <span className="text-gray-500">{cpt.description || ''}</span>
                                            </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              );
                            })()
                          ) : (
                            <span>{selectedNote.cpt_code || 'Not set'}</span>
                          )}
                        </div>
                        {/* Add-on CPT picker for the note header.
                            Mirrors the appointment-dialog picker so the
                            provider has the same control everywhere. The
                            note's selected_note.cpt_code_id (resolved
                            above) feeds the primary filter; edits sync
                            to the linked appointment automatically on
                            save (server.update_note triggers
                            sync_appt_note_addons). */}
                        {(selectedNote.cpt_code_id || selectedNote.cpt_code) && (
                          <AddonCptPicker
                            primaryCptId={
                              selectedNote.cpt_code_id ||
                              (cptCodes || []).find(c => c.code === selectedNote.cpt_code)?.id
                            }
                            selectedIds={
                              Array.isArray(selectedNote.addon_cpt_code_ids) && selectedNote.addon_cpt_code_ids.length
                                ? selectedNote.addon_cpt_code_ids
                                : []
                            }
                            onChange={(ids) => {
                              setSelectedNote(prev => ({
                                ...prev,
                                addon_cpt_code_ids: ids,
                              }));
                            }}
                            cptCodes={cptCodes}
                            testIdPrefix="note-addon"
                            label="Add-on CPTs"
                            helpText="Same modifiers as primary will be applied. Mod 25 added to primary automatically. Changes sync to the linked appointment."
                          />
                        )}
                        {/* POS & Modality — for billable notes without appointment/consent */}
                        {(() => {
                          const template = formTemplates.find(t => t.id === selectedNote.template_id);
                          const isBillable = template?.billable || selectedNote.billable;
                          const hasConsent = selectedNote.telehealth_consent?.pos_code;
                          if (!isBillable || hasConsent) return null;
                          
                          const effectivePosCodes = getEffectivePosCodes(organizations, posCodes);
                          const selectedProvider = providers?.find(p => p.id === selectedNote.provider_id) || users?.find(u => u.id === selectedNote.provider_id);
                          const providerPosCodes = selectedProvider?.assigned_pos_codes || [];
                          const availablePosCodes = providerPosCodes.length > 0
                            ? effectivePosCodes.filter(p => providerPosCodes.includes(p.code))
                            : effectivePosCodes;
                          const currentPos = selectedNote.pos_code || '';
                          const isTelehealthPos = currentPos === '02' || currentPos === '10';
                          
                          return (
                            <>
                              <span className="text-gray-300">|</span>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">POS:</span>
                                {isEditingNote ? (
                                  <select
                                    value={currentPos}
                                    onChange={(e) => {
                                      const newPos = e.target.value;
                                      const selectedProv = providers?.find(p => p.id === selectedNote.provider_id) || users?.find(u => u.id === selectedNote.provider_id);
                                      const updates = { pos_code: newPos };
                                      if (newPos === '02' || newPos === '10') {
                                        if (!selectedNote.modality) {
                                          updates.modality = selectedProv?.default_telehealth_modality || 'audio_visual';
                                        }
                                      } else {
                                        updates.modality = null;
                                      }
                                      setSelectedNote(prev => ({...prev, ...updates}));
                                      // Sync to consent
                                      const posObj = (getEffectivePosCodes(organizations, posCodes)).find(p => p.code === newPos);
                                      setEditedNoteConsent(prev => {
                                        const updated = {
                                          ...(prev || {}),
                                          pos_code: newPos,
                                          pos_description: posObj?.description || ''
                                        };
                                        if (newPos === '02' || newPos === '10') {
                                          if (!prev?.modality) updated.modality = selectedProv?.default_telehealth_modality || 'audio_visual';
                                          if (!prev?.provider_location && selectedProv?.default_location) updated.provider_location = selectedProv.default_location;
                                        }
                                        return updated;
                                      });
                                    }}
                                    className="h-7 text-sm border rounded px-1"
                                    data-testid="note-edit-pos-selector"
                                  >
                                    <option value="">Select POS</option>
                                    {availablePosCodes.map(pos => (
                                      <option key={pos.code} value={pos.code}>
                                        {pos.code} - {pos.name || pos.description}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span>
                                    {currentPos ? `${currentPos} - ${effectivePosCodes.find(p => p.code === currentPos)?.name || effectivePosCodes.find(p => p.code === currentPos)?.description || ''}` : 'Not set'}
                                  </span>
                                )}
                              </div>
                              {isTelehealthPos && (
                                <>
                                  <span className="text-gray-300">|</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Modality:</span>
                                    {isEditingNote ? (
                                      <select
                                        value={selectedNote.modality || 'audio_visual'}
                                        onChange={(e) => {
                                          setSelectedNote(prev => ({...prev, modality: e.target.value}));
                                          setEditedNoteConsent(prev => ({...(prev || {}), modality: e.target.value}));
                                        }}
                                        className="h-7 text-sm border rounded px-1"
                                        data-testid="note-edit-modality-selector"
                                      >
                                        <option value="audio_visual">Audio-Visual (95)</option>
                                        <option value="audio_only">Audio Only (93)</option>
                                      </select>
                                    ) : (
                                      <span>{selectedNote.modality === 'audio_only' ? 'Audio Only (93)' : 'Audio-Visual (95)'}</span>
                                    )}
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })()}
                        {/* Create & Link Appointment button — for saved notes without appointment */}
                        {!selectedNote.appointment_id && selectedNote.id && (selectedNote.date_of_service || selectedNote.start_time) && selectedNote.cpt_code && (
                          <>
                            <span className="text-gray-300">|</span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid="create-link-appointment-btn"
                              onClick={async () => {
                                const doCreate = async (force = false) => {
                                  const url = `${API}/notes/${selectedNote.id}/create-linked-appointment${force ? '?force=true' : ''}`;
                                  return axios.post(url);
                                };
                                // Flush any pending header edits (CPT, DOS, start/end,
                                // units, POS, modality) to the DB *before* calling the
                                // appointment-create endpoint. The endpoint reads the
                                // note from the DB, so if the user picked a CPT seconds
                                // ago the autosave debounce may not have fired yet and
                                // the backend would say "Note has no CPT code".
                                try {
                                  const flushPayload = {};
                                  if (selectedNote.cpt_code) flushPayload.cpt_code = selectedNote.cpt_code;
                                  if (selectedNote.cpt_code_id) flushPayload.cpt_code_id = selectedNote.cpt_code_id;
                                  if (selectedNote.date_of_service) flushPayload.date_of_service = selectedNote.date_of_service;
                                  if (selectedNote.start_time) flushPayload.start_time = selectedNote.start_time;
                                  if (selectedNote.end_time) flushPayload.end_time = selectedNote.end_time;
                                  if (selectedNote.units) flushPayload.units = selectedNote.units;
                                  if (selectedNote.pos_code) flushPayload.pos_code = selectedNote.pos_code;
                                  if (selectedNote.modality) flushPayload.modality = selectedNote.modality;
                                  if (Object.keys(flushPayload).length > 0) {
                                    await axios.put(`${API}/notes/${selectedNote.id}`, flushPayload);
                                  }
                                } catch (flushErr) {
                                  console.warn('[Create&Link] Pre-flush save failed, continuing anyway:', flushErr);
                                }
                                try {
                                  const resp = await doCreate(false);
                                  toast.success('Appointment created and linked');
                                  setSelectedNote(prev => ({
                                    ...prev,
                                    appointment_id: resp.data.appointment_id,
                                    ...(resp.data.telehealth_consent_created ? {
                                      telehealth_consent: {
                                        consent_given: true,
                                        pos_code: prev.pos_code,
                                        modality: prev.modality || 'audio_visual',
                                      }
                                    } : {})
                                  }));
                                  // Optimistically expose the new appointment so the
                                  // linked-appointment card in the note header renders
                                  // immediately, without waiting for fetchAppointments()
                                  // to round-trip.
                                  if (resp.data.appointment) {
                                    setNoteLinkedAppointment(resp.data.appointment);
                                    // Feb 27, 2026 — Also push the new appointment
                                    // into the global `appointments` state so it
                                    // shows up on the calendar / past appointments
                                    // list immediately.  Previously only the
                                    // note's local card got updated; the next
                                    // `fetchAppointments()` call uses a tight
                                    // ±1–21-day window centered on TODAY (not the
                                    // note's DOS), so retroactively-created
                                    // appointments whose DOS was older than that
                                    // window disappeared from the calendar /
                                    // chart's appointments list.  Dedupe on id
                                    // so a slower refetch doesn't double-insert.
                                    setAppointments(prev => {
                                      if (!resp.data.appointment.id) return prev;
                                      if (prev.some(a => a.id === resp.data.appointment.id)) return prev;
                                      return [resp.data.appointment, ...prev];
                                    });
                                  }
                                  if (typeof fetchAppointments === 'function') fetchAppointments();
                                } catch (err) {
                                  const status = err.response?.status;
                                  const detail = err.response?.data?.detail;
                                  // 409 with structured detail → conflict. Prompt user.
                                  if (status === 409 && detail && typeof detail === 'object') {
                                    if (detail.can_force) {
                                      const confirmMsg = `${detail.message}\n\nCreate the appointment anyway?`;
                                      if (window.confirm(confirmMsg)) {
                                        try {
                                          const resp2 = await doCreate(true);
                                          toast.success('Appointment created and linked (conflict overridden)');
                                          setSelectedNote(prev => ({
                                            ...prev,
                                            appointment_id: resp2.data.appointment_id,
                                            ...(resp2.data.telehealth_consent_created ? {
                                              telehealth_consent: {
                                                consent_given: true,
                                                pos_code: prev.pos_code,
                                                modality: prev.modality || 'audio_visual',
                                              }
                                            } : {})
                                          }));
                                          if (resp2.data.appointment) {
                                            setNoteLinkedAppointment(resp2.data.appointment);
                                            // Same optimistic merge as the
                                            // non-force branch above — see
                                            // Feb 27, 2026 comment.
                                            setAppointments(prev => {
                                              if (!resp2.data.appointment.id) return prev;
                                              if (prev.some(a => a.id === resp2.data.appointment.id)) return prev;
                                              return [resp2.data.appointment, ...prev];
                                            });
                                          }
                                          if (typeof fetchAppointments === 'function') fetchAppointments();
                                          return;
                                        } catch (err2) {
                                          const d2 = err2.response?.data?.detail;
                                          toast.error(typeof d2 === 'object' ? d2.message : (d2 || 'Failed to create appointment'));
                                          return;
                                        }
                                      }
                                      return; // user cancelled
                                    }
                                    // Non-bypassable conflict (CPT same-day etc.)
                                    toast.error(detail.message || 'Scheduling conflict');
                                    return;
                                  }
                                  toast.error(typeof detail === 'object' ? (detail?.message || 'Failed to create appointment') : (detail || 'Failed to create appointment'));
                                }
                              }}
                            >
                              <CalendarPlus className="h-3.5 w-3.5" />
                              Create & Link Appointment
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Chevron Stepper Workflow Status + Lock/Duplicate */}
                <div className="px-4 py-3 border-b">
                  <div className="flex items-center justify-between">
                  {(() => {
                    // Get template signature requirements
                    const template = formTemplates.find(t => t.id === selectedNote.template_id);
                    const requiresProviderSig = template?.requires_provider_signature !== false;
                    const requiresSupervisorSig = template?.requires_supervisor_signature || false;
                    const requiresClientSig = template?.require_client_signature || false;
                    const isBillableNote = template?.billable || selectedNote.billable;
                    
                    const providerHasSigned = !!selectedNote.provider_signed_at;
                    const supervisorHasSigned = !!selectedNote.supervisor_signed_at && 
                      (!selectedNote.assigned_supervisor_2 || !!selectedNote.supervisor_2_signed_at);
                    const clientHasSigned = !!selectedNote.client_signed_at;
                    
                    // Build workflow steps dynamically
                    const workflowSteps = [
                      { value: 'draft', label: 'Draft' }
                    ];
                    
                    if (requiresProviderSig) {
                      workflowSteps.push({ 
                        value: 'provider_signed', 
                        label: providerHasSigned ? 'Provider Signed' : 'Provider Sign'
                      });
                    }
                    
                    if (requiresSupervisorSig) {
                      workflowSteps.push({ 
                        value: 'sent_to_supervisor', 
                        label: supervisorHasSigned ? 'Supervisor Signed' : 'Supervisor Sign'
                      });
                    }
                    
                    if (requiresClientSig) {
                      workflowSteps.push({ 
                        value: 'sent_to_client_for_signature', 
                        label: clientHasSigned ? 'Client Signed' : 'Client Sign'
                      });
                    }
                    
                    if (isBillableNote) {
                      workflowSteps.push(
                        { value: 'signed_ready_for_billing', label: 'Ready for Billing' },
                        { value: 'billed', label: 'Billed' },
                        { value: 'paid', label: 'Paid' }
                      );
                    } else {
                      workflowSteps.push({ value: 'completed', label: 'Completed' });
                    }
                    
                    const currentStatus = selectedNote.status || 'draft';
                    
                    // Determine step states
                    const getStepState = (step, index) => {
                      const statusOrder = workflowSteps.map(s => s.value);
                      const currentIndex = statusOrder.indexOf(currentStatus);
                      
                      if (step.value === 'provider_signed') {
                        return providerHasSigned ? 'completed' : (currentStatus === 'draft' ? 'current' : 'pending');
                      }
                      if (step.value === 'sent_to_supervisor') {
                        return supervisorHasSigned ? 'completed' : (providerHasSigned && !supervisorHasSigned ? 'current' : 'pending');
                      }
                      if (step.value === 'sent_to_client_for_signature') {
                        return clientHasSigned ? 'completed' : 'pending';
                      }
                      
                      if (step.value === currentStatus) return 'current';
                      if (index < currentIndex) return 'completed';
                      return 'pending';
                    };
                    
                    return (
                      <div className="flex items-center">
                        {workflowSteps.map((step, index) => {
                          const state = getStepState(step, index);
                          const isLast = index === workflowSteps.length - 1;
                          
                          return (
                            <div key={step.value} className="flex items-center">
                              {/* Step */}
                              <button
                                onClick={async (e) => {
                                  if (step.value === 'provider_signed' && !providerHasSigned) {
                                    await handleSignNote('provider', true, null, e.shiftKey);
                                    return;
                                  }
                                  if (step.value === 'sent_to_supervisor' && !supervisorHasSigned) {
                                    // Jun 2026 — if the current user is NOT the assigned
                                    // supervisor, route the note to them instead of
                                    // stamping a signature that isn't theirs.
                                    const isAssignedSup = user?.id === selectedNote.assigned_supervisor
                                      || user?.id === selectedNote.assigned_supervisor_2;
                                    const hasAssignedSup = selectedNote.assigned_supervisor || selectedNote.assigned_supervisor_2;
                                    if (hasAssignedSup && !isAssignedSup) {
                                      try {
                                        await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'pending_supervisor_signature' });
                                        toast.success('Routed to the assigned supervisor for signature');
                                        setSelectedNote(prev => ({ ...prev, status: 'pending_supervisor_signature' }));
                                        refreshNotes(); refreshTodos();
                                      } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Failed to route to supervisor');
                                      }
                                      return;
                                    }
                                    // Actually sign as supervisor (same as provider signing)
                                    await handleSignNote('supervisor', true, null, e.shiftKey);
                                    return;
                                  }
                                  if (step.value === 'sent_to_client_for_signature') {
                                    setShowSendToClientDialog(true);
                                    return;
                                  }
                                  if (step.value === currentStatus) return;
                                  
                                  try {
                                    await axios.put(`${API}/notes/${selectedNote.id}`, { status: step.value });
                                    toast.success(`Status updated to ${step.label}`);
                                    setSelectedNote(prev => ({ ...prev, status: step.value }));
                                    refreshNotes(); refreshTodos();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to update status');
                                  }
                                }}
                                className={`
                                  relative px-3 py-1.5 text-xs font-medium transition-all h-8 flex items-center
                                  ${state === 'completed' ? 'bg-teal-600 text-white' : ''}
                                  ${state === 'current' ? 'bg-teal-500 text-white font-semibold' : ''}
                                  ${state === 'pending' ? 'bg-gray-100 text-gray-500' : ''}
                                  ${index === 0 ? 'rounded-l-md' : ''}
                                  ${isLast ? 'rounded-r-md' : ''}
                                  hover:opacity-80
                                `}
                                style={{
                                  clipPath: isLast ? undefined : 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)',
                                  marginLeft: index > 0 ? '-4px' : '0',
                                  paddingLeft: index > 0 ? '12px' : '8px',
                                  paddingRight: isLast ? '8px' : '16px'
                                }}
                              >
                                {state === 'completed' && <Check className="inline h-3 w-3 mr-1" />}
                                {step.label}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* Lock & Duplicate buttons */}
                  <div className="flex items-center gap-1 ml-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-7 px-2 ${selectedNote.is_locked || selectedNote.status === 'locked' ? 'text-amber-600 hover:text-amber-700' : 'text-gray-400 hover:text-gray-600'}`}
                      title={selectedNote.is_locked || selectedNote.status === 'locked' ? "Unlock Note" : "Lock Note"}
                      onClick={async () => {
                        const isCurrentlyLocked = selectedNote.is_locked || selectedNote.status === 'locked';
                        const confirmMessage = isCurrentlyLocked 
                          ? 'Are you sure you want to unlock this note? It will become editable again.'
                          : 'Are you sure you want to lock this note? It will prevent further edits.';
                        if (!window.confirm(confirmMessage)) return;
                        try {
                          const newStatus = isCurrentlyLocked ? 'draft' : 'locked';
                          const noteId = selectedNote.id;
                          updateNoteStatusOptimistically(noteId, newStatus, { is_locked: !isCurrentlyLocked });
                          await axios.put(`${API}/notes/${noteId}`, { status: newStatus, is_locked: !isCurrentlyLocked });
                          toast.success(isCurrentlyLocked ? 'Note unlocked' : 'Note locked');
                          setSelectedNote({...selectedNote, status: newStatus, is_locked: !isCurrentlyLocked});
                          refreshNotes(); refreshTodos();
                        } catch (error) {
                          console.error('Error toggling lock:', error);
                          toast.error('Failed to update lock status');
                          refreshNotes(); refreshTodos();
                          refreshClientNotes();
                        }
                      }}
                    >
                      {selectedNote.is_locked || selectedNote.status === 'locked' ? (
                        <Lock className="h-4 w-4" />
                      ) : (
                        <Unlock className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-gray-500 hover:text-teal-600"
                      title="Duplicate Note"
                      onClick={async () => {
                        try {
                          const fullNoteResponse = await axios.get(`${API}/notes/${selectedNote.id}`);
                          const sourceNote = fullNoteResponse.data;
                          if (!sourceNote) { toast.error('Could not fetch note data'); return; }
                          const duplicateData = {
                            client_id: sourceNote.client_id, client_ids: sourceNote.client_ids || [],
                            is_group_note: sourceNote.is_group_note || false,
                            provider_id: sourceNote.provider_id || user?.id, template_id: sourceNote.template_id,
                            content: sourceNote.content || '', status: 'draft',
                            note_type: sourceNote.note_type, form_type_name: sourceNote.form_type_name || sourceNote.form_type,
                            title: sourceNote.title, date_of_service: sourceNote.date_of_service,
                            start_time: sourceNote.start_time, end_time: sourceNote.end_time,
                            units: sourceNote.units, cpt_code: sourceNote.cpt_code,
                            billable: sourceNote.billable || false, linked_goals: sourceNote.linked_goals || [],
                            telehealth_consent: sourceNote.telehealth_consent,
                            require_client_signature: sourceNote.require_client_signature || false,
                            allow_client_signature: sourceNote.allow_client_signature || false,
                            requires_supervisor_signature: sourceNote.requires_supervisor_signature || false,
                            assigned_supervisor: sourceNote.assigned_supervisor || null
                          };
                          const response = await axios.post(`${API}/notes`, duplicateData);
                          toast.success('Note duplicated successfully');
                          refreshNotes(); refreshTodos();
                          await openNote(response.data);
                        } catch (error) {
                          console.error('Error duplicating note:', error);
                          toast.error('Failed to duplicate note: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  </div>
                </div>
                
                {/* Warning/Action for notes without clients - only if note truly has no client_id */}
                {hasNoClient && (
                  <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-red-600 font-bold text-lg">⚠️ No Client Assigned</span>
                    </div>
                    <p className="text-sm text-red-700 mb-3">
                      This note must be assigned to a client. Please select a client below:
                    </p>
                    <div className="space-y-2">
                      <Select onValueChange={async (clientId) => {
                        try {
                          await axios.put(`${API}/notes/${selectedNote.id}`, {
                            client_id: clientId,
                            is_group_note: false
                          });
                          toast.success("Client assigned successfully");
                          refreshNotes(); refreshTodos();
                          setShowNoteDetail(false);
                        } catch (error) {
                          toast.error("Failed to assign client");
                        }
                      }}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select a client..." />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.filter(c => !c.is_archived).map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.full_name} (MRN: {formatMrn(client.mrn)})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-red-600">
                        Once assigned, you can continue editing this note.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Draft Recovery Banner — shown in BOTH edit and view mode
                    (read-only opens from billing must still surface drafts;
                    restore only displays the draft, saves stay edit-gated) */}
                {noteEditingAutosave.showDraftRecovery && (
                  <DraftRecoveryBanner
                    isVisible={true}
                    onRestore={noteEditingAutosave.restoreFromDraft}
                    onDismiss={noteEditingAutosave.dismissDraftRecovery}
                    draft={noteEditingAutosave.recoveredDraft}
                  />
                )}
                
                <Card>
                  <CardContent className="p-6">
                  {(() => {
                    // Guard against null selectedNote.
                    // Jun 2026 — also hold while a local draft is being
                    // validated/restored on open (parity with V2NoteHost):
                    // the user only ever sees the fully-restored content.
                    if (!selectedNote || noteEditingAutosave.draftRestorePending) {
                      return <div className="flex justify-center py-6"><div className="h-8 w-8 animate-spin rounded-full border-[3px] border-gray-300 border-t-teal-600" aria-label="Loading" /></div>;
                    }
                    
                    // Try to parse content as JSON (for template-based notes)
                    try {
                      // In edit mode, use editedNoteContent; otherwise parse from selectedNote
                      let noteData;
                      
                      // CRITICAL: Always use editedNoteContent when editing to prevent data loss
                      // Even if it seems empty, it may contain the user's unsaved work
                      if (isEditingNote) {
                        void 0 && console.log('[Note Edit Debug] isEditingNote=true');
                        void 0 && console.log('[Note Edit Debug] selectedNote.template_id:', selectedNote.template_id);
                        void 0 && console.log('[Note Edit Debug] selectedNote.template_name:', selectedNote.template_name);
                        void 0 && console.log('[Note Edit Debug] editedNoteContent:', editedNoteContent ? 'exists' : 'null');
                        void 0 && console.log('[Note Edit Debug] editedNoteContent.fields?.length:', editedNoteContent?.fields?.length);
                        void 0 && console.log('[Note Edit Debug] editedNoteContent.template_id:', editedNoteContent?.template_id);
                        void 0 && console.log('[Note Edit Debug] editedNoteContent.template_name:', editedNoteContent?.template_name);
                        
                        // If we have editedNoteContent with actual structure, use it
                        if (editedNoteContent && (editedNoteContent.fields?.length > 0 || editedNoteContent.sections?.length > 0)) {
                          noteData = editedNoteContent;
                          void 0 && console.log('[Note Edit Debug] Using editedNoteContent, fields:', noteData.fields?.length);
                        } else {
                          // Fallback to server content only if editedNoteContent is truly empty
                          console.warn('[Note] editedNoteContent is empty during edit mode, falling back to server content');
                          const parsedContent = JSON.parse(selectedNote.content || '{}');
                          
                          // If parsed content also has no fields, try to initialize from template
                          if (parsedContent && (parsedContent.fields?.length > 0 || parsedContent.sections?.length > 0)) {
                            noteData = parsedContent;
                          } else if (selectedNote.template_id) {
                            console.warn('[Note] Server content also empty, initializing from template:', selectedNote.template_id, selectedNote.template_name);
                            const template = formTemplates.find(t => t.id === selectedNote.template_id);
                            if (template && template.form_fields && template.form_fields.length > 0) {
                              noteData = {
                                template_id: selectedNote.template_id,
                                template_name: template.name || selectedNote.template_name,
                                fields: template.form_fields.map(field => ({
                                  ...field,
                                  value: field.default_value || '',
                                }))
                              };
                              // Also update editedNoteContent so future saves work correctly
                              setEditedNoteContent(noteData);
                            } else {
                              console.error('[Note] Template not found or has no fields:', selectedNote.template_id);
                              noteData = parsedContent;
                            }
                          } else {
                            noteData = parsedContent;
                          }
                        }
                      } else {
                        // VIEW MODE: Use editedNoteContent if available (set by openNote), 
                        // otherwise parse content as fallback
                        // IMPORTANT: openNote already parses and merges content with template,
                        // so we should use that result instead of re-parsing
                        if (editedNoteContent && (editedNoteContent.fields?.length > 0 || editedNoteContent.sections?.length > 0)) {
                          noteData = editedNoteContent;
                          void 0 && console.log('[VIEW MODE DEBUG] Using editedNoteContent (set by openNote), fields:', editedNoteContent.fields?.length);
                        } else {
                          // Fallback: parse content directly
                          let parsedContent = {};
                          try {
                            const contentStr = selectedNote.content || '{}';
                            // Handle cases where content is literally "null" string
                            if (contentStr && contentStr !== 'null' && contentStr !== 'undefined') {
                              parsedContent = JSON.parse(contentStr);
                            }
                          } catch (parseError) {
                            console.error('[VIEW MODE] Error parsing content:', parseError);
                            parsedContent = {};
                          }
                          void 0 && console.log('[VIEW MODE DEBUG] parsedContent from selectedNote:', parsedContent);
                          void 0 && console.log('[VIEW MODE DEBUG] parsedContent.fields?.length:', parsedContent?.fields?.length);
                          void 0 && console.log('[VIEW MODE DEBUG] parsedContent.sections?.length:', parsedContent?.sections?.length);
                          
                          // Check if parsedContent has actual fields/sections
                          if (parsedContent && (parsedContent.fields?.length > 0 || parsedContent.sections?.length > 0)) {
                            noteData = parsedContent;
                            void 0 && console.log('[VIEW MODE DEBUG] Using parsedContent directly');
                          } else if (selectedNote.template_id) {
                            // Empty content - initialize from template
                            const template = formTemplates.find(t => t.id === selectedNote.template_id);
                            if (template && template.form_fields && template.form_fields.length > 0) {
                              noteData = {
                                template_id: selectedNote.template_id,
                                template_name: template.name,
                                fields: template.form_fields.map(field => ({
                                  ...field,  // Include ALL field properties (rowLabels, columnHeaders, etc.)
                                  value: field.default_value || '',
                                }))
                              };
                            } else {
                              noteData = { fields: [] };
                            }
                          } else {
                            noteData = { fields: [] };
                          }
                        }
                      }
                      
                      
                      // Main rendering logic - handle both old (fields) and new (sections) structures
                      // Jun 2026 — also render structured content that lacks a
                      // template_name (some portal/intake docs store {fields:[...]}
                      // only); previously these fell through to the raw-JSON dump.
                      if (noteData.template_name || noteData.fields?.length > 0 || noteData.sections?.length > 0) {
                        // ============================================================
                        // NEW: Use FormFieldRenderer for unified view/edit rendering
                        // This replaces the legacy renderField function (~1100 lines)
                        // ============================================================
                        
                        // Build form data object for the renderer
                        const formDataForRenderer = {};
                        
                        // Extract field values from noteData into flat formData object
                        if (noteData.sections) {
                          noteData.sections.forEach(section => {
                            (section.fields || []).forEach(field => {
                              if (field.id && field.value !== undefined) {
                                formDataForRenderer[field.id] = field.value;
                              }
                            });
                          });
                        } else if (noteData.fields) {
                          noteData.fields.forEach(field => {
                            if (field.id && field.value !== undefined) {
                              formDataForRenderer[field.id] = field.value;
                            }
                          });
                        }
                        
                        void 0 && console.log('[VIEW MODE DEBUG] formDataForRenderer entries:', Object.keys(formDataForRenderer).length);
                        void 0 && console.log('[VIEW MODE DEBUG] formDataForRenderer sample:', Object.entries(formDataForRenderer).slice(0, 3));
                        
                        // Build context for FormFieldRenderer (similar to note creation)
                        // For selectedClient, prefer the fully-loaded selectedClient from ClientsContext
                        // if it matches the note's client, otherwise fall back to clients.find()
                        const noteClientId = selectedNote?.client_ids?.[0] || selectedNote?.client_id;
                        const fullSelectedClient = selectedClient?.id === noteClientId 
                          ? selectedClient  // Use fully-loaded client with allergies, etc.
                          : allClientsForLookup.find(c => c.id === noteClientId);
                        
                        const viewFormFieldContext = {
                          // Client data - use fully loaded client if available
                          selectedClient: fullSelectedClient,
                          clients: allClientsForLookup,  // Use full client list for lookups, not filtered list
                          selectedNoteClients: selectedNote?.client_ids || [selectedNote?.client_id].filter(Boolean),
                          clientDiagnoses: clientDiagnoses,
                          // PHI-fix May 2026 — filter to the NOTE's
                          // client_id so the snapshot fallback in
                          // specialty/index.js (when field.value is
                          // empty) can NEVER render meds belonging to
                          // another patient.  ``clientMedications`` in
                          // the parent is the global last-opened-chart
                          // state; without this filter the renderer
                          // would display whatever client was last in
                          // chart, NOT the note's actual client.
                          clientMedications: (clientMedications || []).filter(m => m.client_id === noteClientId),
                          clientVitals: clientVitals || [],
                          
                          // User data
                          user: user,
                          users: users,
                          // The NOTE's provider (for current_provider_name fields)
                          noteProvider: (users || []).find(u => u.id === selectedNote?.provider_id)
                            || (selectedNote?.provider_name ? { full_name: selectedNote.provider_name } : null),
                          
                          // Appointments
                          appointments: clientAppointments.length > 0 ? clientAppointments : appointments,
                          
                          // Form data - use extracted values from noteData fields
                          // FormFieldRenderer will read field.value directly from the fields
                          newNoteFormData: { ...formDataForRenderer, ...editorMetaFlags },
                          formTemplates: formTemplates,
                          
                          // Treatment Plan data
                          linkedGoals: selectedNote?.linked_goals || linkedGoals || [],
                          treatmentPlanGoals: availableTreatmentPlanGoals || [],
                          availablePANs: availablePANs || [],
                          
                          // Telehealth/Consent
                          telehealthConsent: isEditingNote ? editedNoteConsent : (selectedNote?.telehealth_consent || {}),
                          setTelehealthConsent: isEditingNote ? setEditedNoteConsent : () => {},
                          organizations: organizations,
                          posCodes: organizations?.[0]?.pos_codes || [],
                          
                          // Dropdown data
                          serviceTypes: serviceTypes,
                          locations: locations,
                          providers: users?.filter(u => u.is_provider) || [],
                          allGroups: clientGroups || [],
                          
                          // Group note data
                          isGroupNote: selectedNote?.is_group_note || false,
                          groupMembers: [],
                          groupAttendance: {},
                          groupMemberNotes: {},
                          
                          // DEBUG: Log isEditingNote value
                          
                          // Note state
                          selectedNote: selectedNote,
                          isEditMode: isEditingNote,
                          disabled: !isEditingNote,
                          mode: isEditingNote ? 'edit' : 'view',
                          
                          // Use the legacy updateFieldValue function for compatibility
                          // This ensures edits are saved in the correct format.
                          // Plan B: routed through the lifted, memoized
                          // handleFieldChangeLifted / setNewNoteFormDataBridge
                          // so their function identities are stable across
                          // App re-renders (was: inline arrows recreated every
                          // render → renderer subtree saw "new" props on every
                          // keystroke even when nothing logically changed).
                          handleFieldChange: isEditingNote ? handleFieldChangeLifted : () => {},
                          setNewNoteFormData: isEditingNote ? setNewNoteFormDataWithMeta : () => {},
                          setSelectedClient: setSelectedClient,
                          setShowDiagnosisForm: setShowDiagnosisForm,
                          setShowMedicationForm: setShowMedicationForm,
                          setEditingMedication: setEditingMedication,
                          setMedPlanCapture: setMedPlanCapture,
                          setMedSnapshotCapture: setMedSnapshotCapture,
                          setShowLinkGoalsDialog: setShowLinkGoalsDialog,
                          fetchTreatmentPlanGoals: fetchTreatmentPlanGoals,
                          setLinkedGoals: setLinkedGoals,
                          
                          // Signature helpers
                          loadedSignatures: loadedSignatures,
                          setLoadedSignatures: setLoadedSignatures,
                          getSignatureUrl: getSignatureUrl,
                          currentUser: user,
                        };
                        
                        // Get fields/sections for renderer
                        const fieldsForRenderer = noteData.sections 
                          ? null  // Use sections prop
                          : (noteData.fields || []);
                        const sectionsForRenderer = noteData.sections || null;
                        
                        return (
                          <div className="space-y-4">
                            {/* Warning: Editing a client-signed document invalidates the signature */}
                            {isEditingNote && selectedNote?.client_signed_at && (
                              <div data-testid="signature-invalidation-warning" className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-300 rounded-md">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div>
                                  <p className="text-sm font-semibold text-amber-800">Client Signature Will Be Invalidated</p>
                                  <p className="text-xs text-amber-700 mt-0.5">
                                    This document was signed by the client on {new Date(selectedNote.client_signed_at).toLocaleDateString()}. 
                                    Any changes you save will require a new client signature.
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* Signature was invalidated — offer revert */}
                            {selectedNote?.signature_history?.length > 0 && !selectedNote?.client_signed_at && (() => {
                              const lastEntry = selectedNote.signature_history[selectedNote.signature_history.length - 1];
                              return lastEntry?.action === 'signature_invalidated';
                            })() && (
                              <div data-testid="signature-invalidated-banner" className="flex items-start gap-3 p-3 bg-red-50 border border-red-300 rounded-md">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-red-800">Client Signature Was Invalidated</p>
                                  <p className="text-xs text-red-700 mt-0.5">
                                    This document was modified after the client signed it. A new client signature is required.
                                  </p>
                                  <button
                                    data-testid="revert-to-signed-btn"
                                    className="mt-2 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded border border-red-300 transition-colors"
                                    onClick={async () => {
                                      if (!window.confirm('Revert this document to the last signed version? This will undo all changes made since the client signed.')) return;
                                      try {
                                        let resp;
                                        try {
                                          resp = await axios.post(
                                            `${API}/notes/${selectedNote.id}/revert-to-signed`,
                                            {},
                                          );
                                        } catch (err) {
                                          resp = err.response || { status: 0, data: {} };
                                        }
                                        if (resp.status >= 200 && resp.status < 300) {
                                          const updatedNote = resp.data;
                                          setSelectedNote(updatedNote);
                                          setNotes(prev => prev.map(n => n.id === updatedNote.id ? updatedNote : n));
                                          toast.success('Reverted to signed version. Client signature restored.');
                                        } else if (resp.status === 422) {
                                          // Feb 2026: no usable version snapshot.  Admins get a
                                          // follow-up confirm to force-reinstate the signature
                                          // without touching content (audit trail records it).
                                          const err = resp.data || {};
                                          const isAdmin = (user?.role || '').toLowerCase() === 'admin' || (user?.role || '').toLowerCase() === 'super admin';
                                          if (isAdmin) {
                                            const proceed = window.confirm(
                                              (err.detail || 'No content snapshot is available.')
                                              + '\n\nReinstate the client signature WITHOUT changing content? '
                                              + 'The audit trail will record this admin override.'
                                            );
                                            if (!proceed) return;
                                            let force;
                                            try {
                                              force = await axios.post(
                                                `${API}/notes/${selectedNote.id}/revert-to-signed?force=true`,
                                                {},
                                              );
                                            } catch (ferr) {
                                              force = ferr.response || { status: 0, data: {} };
                                            }
                                            if (force.status >= 200 && force.status < 300) {
                                              const updatedNote = force.data;
                                              setSelectedNote(updatedNote);
                                              setNotes(prev => prev.map(n => n.id === updatedNote.id ? updatedNote : n));
                                              toast.success('Client signature reinstated. Content was not modified.');
                                            } else {
                                              toast.error(force.data?.detail || `Force reinstate failed (HTTP ${force.status})`);
                                            }
                                          } else {
                                            toast.error(err.detail || 'Cannot revert — no version snapshot available. Ask an admin to force-reinstate.');
                                          }
                                        } else {
                                          // Mar 2026 — surface both the status code AND
                                          // the detail so plain "Failed" toasts become
                                          // actionable.
                                          const detail = resp.data?.detail;
                                          toast.error(
                                            detail
                                              ? `Revert failed (${resp.status}): ${detail}`
                                              : `Revert failed (HTTP ${resp.status}). Check DevTools Network tab for details.`
                                          );
                                        }
                                      } catch (e) {
                                        // Mar 2026 — with axios (which auto-attaches auth
                                        // via the global interceptor) this catch should
                                        // now only fire on truly unexpected client-side
                                        // errors.  Still log + surface the exception so
                                        // any future edge case is diagnosable.
                                        console.error('[revert-to-signed] unexpected client-side error:', e);
                                        const msg = (e && (e.message || e.name)) || String(e);
                                        toast.error(`Failed to revert to signed version: ${msg}. Open DevTools → Console for full stack.`);
                                      }
                                    }}
                                  >
                                    Revert to Signed Version
                                  </button>
                                </div>
                              </div>
                            )}
                            <FormFieldRenderer
                              key={selectedNote?.id || 'view-note'}
                              fields={fieldsForRenderer}
                              sections={sectionsForRenderer}
                              contextValue={viewFormFieldContext}
                              mode={isEditingNote ? 'edit' : 'view'}
                              disabled={!isEditingNote}
                            />
                            {/* Jun 2026 legacy-parity #4 — linked-form banners in
                                EDIT mode (previously only in the legacy create
                                overlay, so V2 note editing never surfaced them). */}
                            {isEditingNote && typeof handleAddLinkedForm === 'function' && (() => {
                              const tpl = formTemplates?.find(t => t.id === selectedNote?.template_id);
                              const triggered = (tpl?.linked_forms || []).filter(lf => {
                                const v = formDataForRenderer[lf.trigger_field_id];
                                return Array.isArray(v) ? v.includes(lf.trigger_value) : v === lf.trigger_value;
                              });
                              if (triggered.length === 0) return null;
                              return triggered.map((lf, i) => {
                                const isAdded = linkedFormsAdded?.[lf.form_name];
                                return (
                                  <div key={`${lf.form_name}-${i}`}
                                    className={`w-full mt-3 p-3 rounded-lg border-l-4 ${isAdded ? 'bg-green-50 border-green-500' : 'bg-amber-50 border-amber-500'}`}
                                    data-testid={`linked-form-banner-${i}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{isAdded ? '✅' : '⚠️'}</span>
                                        <span className="text-sm font-medium">
                                          {isAdded
                                            ? `${lf.form_name} added to chart`
                                            : `Based on this answer, please complete a ${lf.form_name}`}
                                        </span>
                                      </div>
                                      {!isAdded && lf.linked_template_id && (
                                        <Button size="sm" variant="outline"
                                          className="bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300"
                                          onClick={() => handleAddLinkedForm(lf, selectedNote)}
                                          data-testid={`linked-form-add-btn-${i}`}>
                                          + Add {lf.form_name}
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                            {/* Jun 2026 legacy-parity — "Create Treatment Plan
                                from PANs" (legacy App.js ~49612).  Shown in EDIT
                                mode when any pan_select field has a value; saves
                                the draft, stashes PANs in sessionStorage and
                                opens the V2 Plan tab (PanImportBanner picks up). */}
                            {isEditingNote && (() => {
                              const allNoteFields = noteData.sections
                                ? noteData.sections.flatMap(s => s.fields || [])
                                : (noteData.fields || []);
                              const pansFromFields = allNoteFields
                                .filter(f => f?.type === 'pan_select')
                                .map(f => formDataForRenderer[f.id])
                                .filter(v => typeof v === 'string' && v.trim())
                                .map((text, idx) => ({ priority: idx + 1, text: text.trim() }));
                              if (pansFromFields.length === 0) return null;
                              const panClientId = selectedNote?.client_ids?.[0] || selectedNote?.client_id;
                              return (
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mt-4" data-testid="create-plan-from-pans-banner">
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-3">
                                      <FileText className="h-6 w-6 text-purple-700" />
                                      <div>
                                        <h4 className="font-medium text-purple-900">Ready to Create Treatment Plan</h4>
                                        <p className="text-sm text-purple-700">{pansFromFields.length} Prioritized Assessed Need{pansFromFields.length > 1 ? 's' : ''} identified</p>
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      className="bg-purple-600 hover:bg-purple-700 text-white"
                                      data-testid="create-plan-from-pans-btn"
                                      onClick={async () => {
                                        try {
                                          if (noteEditingAutosave?.forceSave) {
                                            await noteEditingAutosave.forceSave();
                                          } else {
                                            await axios.put(`${API}/notes/${selectedNote.id}`, {
                                              content: JSON.stringify(editedNoteContent),
                                            });
                                          }
                                        } catch (e) {
                                          toast.error('Could not save the note draft — treatment plan not started. Please try again.');
                                          return;
                                        }
                                        const panClient = selectedClient?.id === panClientId
                                          ? selectedClient
                                          : allClientsForLookup.find(c => c.id === panClientId);
                                        try {
                                          sessionStorage.setItem('pansFromAssessment', JSON.stringify({
                                            clientId: panClientId,
                                            clientName: panClient?.full_name
                                              || `${panClient?.first_name || ''} ${panClient?.last_name || ''}`.trim()
                                              || 'Client',
                                            pans: pansFromFields,
                                            sourceAssessment: selectedNote?.template_name || 'Assessment',
                                          }));
                                        } catch { /* noop */ }
                                        toast.success(`Note saved! Opening Plan tab with ${pansFromFields.length} PAN${pansFromFields.length > 1 ? 's' : ''} ready to import.`);
                                        navigate(`/v2/clients/${panClientId}?tab=treatment-plans`);
                                      }}
                                    >
                                      <FileText className="h-4 w-4 mr-2" />
                                      Create Treatment Plan from PANs
                                    </Button>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                        
                      }
                    } catch (e) {
                      // Not JSON or parsing failed, show as plain text.
                      // Log the real reason — a render-time throw here used
                      // to silently degrade template notes to raw JSON.
                      console.error('[NoteRender] structured render failed, falling back to plain text:', e);
                    }
                    
                    // Show plain text content
                    return <div className="whitespace-pre-wrap">{selectedNote.content}</div>;
                  })()}
                </CardContent>
              </Card>
              
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Version:</strong> {selectedNote.version}</p>
              </div>
              
              {/* Signatures Section at Bottom - Hide for Medical Records Release notes */}
              {selectedNote.form_type_name !== 'Medical Records Release' && (
              <Card className="border-2 border-green-200">
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-base">Signatures</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Admin Override Notice */}
                    {selectedNote.signature_override_reason && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                        <p className="text-red-900 font-semibold mb-2">⚠️ Admin Override Applied</p>
                        <p className="text-sm text-red-800 mb-2">
                          <strong>Reason:</strong> {selectedNote.signature_override_reason}
                        </p>
                        <p className="text-xs text-red-700">
                          Overridden on {new Date(selectedNote.signature_override_at).toLocaleString()} by admin
                        </p>
                      </div>
                    )}
                    
                    {/* Manual-signature attestation — replaces the client
                        signature block when completed via attestation. */}
                    {!selectedNote.client_signed_at && selectedNote.manual_signature_attestation && (
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Client Signature</Label>
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded" data-testid="manual-signature-attestation-block">
                          <p className="text-emerald-900 flex items-center text-sm font-semibold">
                            <span className="mr-2">✅</span>
                            Completed via manual signature attestation
                          </p>
                          <p className="text-xs text-emerald-800 mt-1">
                            Attested by {selectedNote.manual_signature_attestation.attested_by_name || 'staff'} on{' '}
                            {new Date(selectedNote.manual_signature_attestation.attested_at).toLocaleString()}
                          </p>
                          <p className="text-[11px] text-emerald-700 mt-1 italic">
                            {selectedNote.manual_signature_attestation.statement}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Client Signature */}
                    {selectedNote.client_signed_at && (
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Client Signature</Label>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                          {/* Show actual signature image */}
                          {selectedNote.client_signature && (
                            <div className="mb-2 bg-white p-2 border border-gray-200 rounded">
                              <img 
                                src={selectedNote.client_signature} 
                                alt="Client Signature" 
                                className="h-16 w-auto"
                                style={{ imageRendering: 'crisp-edges' }}
                              />
                            </div>
                          )}
                          <p className="text-blue-900 flex items-center text-sm font-semibold">
                            <span className="mr-2">✅</span>
                            {selectedNote.client_signature_name || 'Client'}
                          </p>
                          <p className="text-xs text-blue-700 mt-1">
                            Signed on {new Date(selectedNote.client_signed_at).toLocaleDateString()} at {new Date(selectedNote.client_signed_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Provider Signature - or Complete button if not required */}
                    {(() => {
                      const template = formTemplates.find(t => t.id === selectedNote.template_id);
                      const requiresProviderSig = template?.requires_provider_signature !== false; // Default true
                      
                      // For legacy notes with signature info, always show Provider Signature section
                      const isLegacyWithSignature = selectedNote.is_legacy_import && 
                        (selectedNote.legacy_signature_note || selectedNote.provider_signed_at || selectedNote.legacy_provider_name);
                      
                      // If signature not required AND not a legacy note with signature, show Complete button instead
                      if (!requiresProviderSig && !isLegacyWithSignature) {
                        return (
                          <div>
                            <Label className="text-sm font-semibold mb-2 block">Complete Note</Label>
                            {selectedNote.status === 'completed' ? (
                              <div className="p-3 bg-green-50 border border-green-200 rounded">
                                <p className="text-green-900 flex items-center text-sm font-semibold">
                                  <span className="mr-2">✅</span> Completed
                                </p>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'completed' });
                                      toast.success('Note marked as complete');
                                      refreshNotes(); refreshTodos();
                                      const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                                      setSelectedNote(updatedNote.data);
                                    } catch (error) {
                                      console.error('Failed to complete note:', error);
                                      toast.error('Failed to complete note');
                                    }
                                  }}
                                >
                                  ✓ Mark as Complete
                                </Button>
                                {template?.allow_send_to_client && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                                    onClick={() => setShowSendToClientDialog(true)}
                                  >
                                    📧 Complete & Send to Client
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // Check if user can sign as provider (providers, supervisors, admins, superadmins)
                      // userRoleName is available from top-level useMemo
                      const canSignAsProvider = userRoleName === 'provider' || 
                                                userRoleName === 'supervisor' || 
                                                userRoleName === 'admin' ||
                                                userRoleName === 'super admin' ||
                                                user?.is_provider ||
                                                user?.is_supervisor ||
                                                isSuperAdmin(user);
                      
                      return (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">Provider Signature</Label>
                          {selectedNote.provider_signed_at || selectedNote.provider_signed_by ? (
                        <div 
                          className="p-3 bg-green-50 border border-green-200 rounded cursor-pointer"
                          data-signature-area="provider"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void 0 && console.log('🖱️ Right-clicked provider signature', selectedNote);
                            setSignatureContextMenuPos({ x: e.clientX, y: e.clientY });
                            setSignatureContextMenuType('provider');
                            setShowSignatureContextMenu(true);
                          }}
                          title="Right-click for options"
                        >
                          {/* Show actual signature image — uses the
                              shared SignatureImage helper which
                              transparently swaps ``gcs://`` URIs for
                              a fresh signed URL via the
                              ``/users/{id}/signature`` resolver and
                              survives expired-URL ``onError``
                              events.  May 2026 production fix: the
                              previous inline ``<img src=
                              {selectedNote.provider_signature}>``
                              rendered the raw gcs:// scheme directly
                              and triggered the user-reported "I
                              can't see signatures consistently"
                              bug. */}
                          {(() => {
                            const rawSig = selectedNote.provider_signature || selectedNote.signature || loadedSignatures[selectedNote.provider_signed_by];
                            return (
                              <div className="mb-2 bg-white p-2 border border-gray-200 rounded">
                                <SignatureImage
                                  raw={rawSig}
                                  userId={selectedNote.provider_signed_by}
                                  alt="Provider Signature"
                                  testId="provider-signature-img"
                                />
                              </div>
                            );
                          })()}
                          <p className="text-green-900 flex items-center text-sm font-semibold">
                            <span className="mr-2">✅</span>
                            {(() => {
                              const signer = users.find(u => u.id === selectedNote.provider_signed_by);
                              // For legacy notes, use legacy_provider_name and legacy_provider_credentials
                              if (selectedNote.is_legacy_import) {
                                const legacyName = selectedNote.legacy_provider_name || (signer ? (signer.full_name || `${signer.first_name} ${signer.last_name}`) : 'Unknown Provider');
                                const legacyCredentials = selectedNote.legacy_provider_credentials;
                                return (
                                  <span>
                                    {legacyName}
                                    {legacyCredentials && <span className="text-xs ml-1 text-green-700">{legacyCredentials}</span>}
                                  </span>
                                );
                              }
                              // For regular notes, use system user info
                              return signer ? (
                                <span>
                                  {signer.full_name || `${signer.first_name} ${signer.last_name}`}
                                  {signer.credentials && <span className="text-xs ml-1 text-green-700">{signer.credentials}</span>}
                                </span>
                              ) : 'Signed';
                            })()}
                          </p>
                          {selectedNote.is_legacy_import ? (
                            <p className="text-xs text-blue-600 mt-1 italic">
                              {selectedNote.legacy_signature_note || "Signed in previous system"}
                              {selectedNote.provider_signed_at && ` (${new Date(selectedNote.provider_signed_at).toLocaleDateString()})`}
                            </p>
                          ) : selectedNote.provider_signed_at && !selectedNote.provider_signed_date_hidden ? (
                            <p className="text-xs text-green-700 mt-1">
                              Signed on {new Date(selectedNote.provider_signed_at).toLocaleDateString()} at {new Date(selectedNote.provider_signed_at).toLocaleTimeString()}
                            </p>
                          ) : selectedNote.provider_signed_by ? (
                            <p className="text-xs text-green-700 mt-1">Signed (date not recorded)</p>
                          ) : null}
                          {(selectedNote.provider_signed_by === user.id || isSuperAdmin(user)) && (
                            <div className="mt-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid="provider-remove-signature-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnsignNote('provider');
                                }}
                              >
                                Remove Signature
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : canSignAsProvider ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          data-signature-area="provider"
                          onClick={(e) => handleSignNote('provider', true, null, e.shiftKey)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSignatureContextMenuPos({ x: e.clientX, y: e.clientY });
                            setSignatureContextMenuType('provider');
                            setShowSignatureContextMenu(true);
                          }}
                        >
                          ✍️ Sign as Provider
                          <span className="text-xs text-gray-500 ml-2">(Right-click for options)</span>
                        </Button>
                      ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded text-gray-500 text-sm">
                          Awaiting provider signature
                        </div>
                      )}
                        </div>
                      );
                    })()}
                    
                    {/* Supervisor Signature - show if required by template OR note was sent to supervisor */}
                    {(() => {
                      const template = formTemplates.find(t => t.id === selectedNote.template_id);
                      const requiresSupervisorSig = template?.requires_supervisor_signature || false;
                      
                      // Show supervisor signature section if:
                      // 1. Template requires it, OR
                      // 2. Note was explicitly sent to supervisor for review, OR
                      // 3. Note has an assigned supervisor (routing rules)
                      const noteSentToSupervisor = selectedNote.status === 'sent_to_supervisor' || 
                                                   selectedNote.status === 'pending_supervisor_signature';
                      const noteHasAssignedSupervisor = !!selectedNote.assigned_supervisor;
                      
                      const shouldShowSupervisorSection = requiresSupervisorSig || noteSentToSupervisor || noteHasAssignedSupervisor;
                      
                      if (!shouldShowSupervisorSection) return null; // Hide if not needed
                      
                      // Check if user can sign as supervisor (only supervisors, admins, superadmins)
                      // userRoleName is available from top-level useMemo
                      const canSignAsSupervisor = userRoleName === 'supervisor' || 
                                                  userRoleName === 'admin' ||
                                                  userRoleName === 'super admin' ||
                                                  user?.is_supervisor ||
                                                  isSuperAdmin(user);
                      
                      return (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">Supervisor Signature (if required)</Label>
                          {selectedNote.supervisor_signed_at || selectedNote.supervisor_signed_by ? (
                        <div 
                          className="p-3 bg-green-50 border border-green-200 rounded cursor-pointer"
                          data-signature-area="supervisor"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSignatureContextMenuPos({ x: e.clientX, y: e.clientY });
                            setSignatureContextMenuType('supervisor');
                            setShowSignatureContextMenu(true);
                          }}
                          title="Right-click for options"
                        >
                          {/* Show actual signature image - prefer note's stored signature, fallback to fetched user signature */}
                          {(() => {
                            const rawSig = selectedNote.supervisor_signature || loadedSignatures[selectedNote.supervisor_signed_by];
                            return (
                              <div className="mb-2 bg-white p-2 border border-gray-200 rounded">
                                <SignatureImage
                                  raw={rawSig}
                                  userId={selectedNote.supervisor_signed_by}
                                  alt="Supervisor Signature"
                                  testId="supervisor-signature-img"
                                />
                              </div>
                            );
                          })()}
                          <p className="text-green-900 flex items-center text-sm font-semibold">
                            <span className="mr-2">✅</span>
                            {(() => {
                              const signer = users.find(u => u.id === selectedNote.supervisor_signed_by);
                              return signer ? (
                                <span>
                                  {signer.full_name || `${signer.first_name} ${signer.last_name}`}
                                  {signer.credentials && <span className="text-xs ml-1 text-green-700">{signer.credentials}</span>}
                                </span>
                              ) : 'Signed';
                            })()}
                          </p>
                          {selectedNote.supervisor_signed_at && !selectedNote.supervisor_signed_date_hidden && (
                            <p className="text-xs text-green-700 mt-1">
                              Signed on {new Date(selectedNote.supervisor_signed_at).toLocaleDateString()} at {new Date(selectedNote.supervisor_signed_at).toLocaleTimeString()}
                            </p>
                          )}
                          {(!selectedNote.supervisor_signed_at || selectedNote.supervisor_signed_date_hidden) && selectedNote.supervisor_signed_by && (
                            <p className="text-xs text-green-700 mt-1">Signed (date not recorded)</p>
                          )}
                          {selectedNote.supervisor_signed_by === user.id && (
                            <p className="text-xs text-gray-500 mt-1 italic">Right-click to remove your signature</p>
                          )}
                        </div>
                      ) : canSignAsSupervisor ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          data-signature-area="supervisor"
                          onClick={(e) => handleSignNote('supervisor', true, null, e.shiftKey)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSignatureContextMenuPos({ x: e.clientX, y: e.clientY });
                            setSignatureContextMenuType('supervisor');
                            setShowSignatureContextMenu(true);
                          }}
                        >
                          ✍️ Sign as Supervisor
                          <span className="text-xs text-gray-500 ml-2">(Right-click for options)</span>
                        </Button>
                      ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded text-gray-500 text-sm">
                          Awaiting supervisor signature
                        </div>
                      )}
                        </div>
                      );
                    })()}
                    
                    {/* Second Supervisor Signature - show if assigned_supervisor_2 exists */}
                    {(() => {
                      if (!selectedNote.assigned_supervisor_2) return null;
                      
                      const canSignAsSupervisor2 = user?.id === selectedNote.assigned_supervisor_2 ||
                                                    userRoleName === 'admin' ||
                                                    userRoleName === 'super admin' ||
                                                    isSuperAdmin(user);
                      
                      const sup2 = users.find(u => u.id === selectedNote.assigned_supervisor_2);
                      const sup2Name = sup2?.full_name || `${sup2?.first_name || ''} ${sup2?.last_name || ''}`.trim() || 'Assigned Supervisor';
                      
                      return (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">Additional Supervisor Signature — {sup2Name}</Label>
                          {selectedNote.supervisor_2_signed_at || selectedNote.supervisor_2_signed_by ? (
                        <div 
                          className="p-3 bg-green-50 border border-green-200 rounded cursor-pointer"
                          data-signature-area="supervisor_2"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSignatureContextMenuPos({ x: e.clientX, y: e.clientY });
                            setSignatureContextMenuType('supervisor_2');
                            setShowSignatureContextMenu(true);
                          }}
                          title="Right-click for options"
                        >
                          {(() => {
                            const signer2 = users.find(u => u.id === selectedNote.supervisor_2_signed_by);
                            const sig2Image = selectedNote.supervisor_2_signature || loadedSignatures[selectedNote.supervisor_2_signed_by];
                            
                            if (!sig2Image && selectedNote.supervisor_2_signed_by && !loadedSignatures[selectedNote.supervisor_2_signed_by]) {
                              getSignatureUrl(selectedNote.supervisor_2_signed_by).then(url => {
                                if (url) {
                                  setLoadedSignatures(prev => ({ ...prev, [selectedNote.supervisor_2_signed_by]: url }));
                                }
                              });
                            }
                            
                            return sig2Image ? (
                              <div className="mb-2 bg-white p-2 border border-gray-200 rounded">
                                <img src={sig2Image} alt="Supervisor 2 Signature" className="h-16 w-auto" style={{ imageRendering: 'crisp-edges' }} onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            ) : (
                              <div className="mb-2 p-2 border border-dashed border-gray-300 rounded bg-gray-50 text-center text-gray-500 text-xs">
                                {loadedSignatures[selectedNote.supervisor_2_signed_by] === undefined ? 'Loading signature...' : 'No signature image on file'}
                              </div>
                            );
                          })()}
                          <p className="text-green-900 flex items-center text-sm font-semibold">
                            <span className="mr-2">✅</span>
                            {(() => {
                              const signer2 = users.find(u => u.id === selectedNote.supervisor_2_signed_by);
                              return signer2 ? (
                                <span>
                                  {signer2.full_name || `${signer2.first_name} ${signer2.last_name}`}
                                  {signer2.credentials && <span className="text-xs ml-1 text-green-700">{signer2.credentials}</span>}
                                </span>
                              ) : 'Signed';
                            })()}
                          </p>
                          {selectedNote.supervisor_2_signed_at && !selectedNote.supervisor_2_signed_date_hidden && (
                            <p className="text-xs text-green-700 mt-1">
                              Signed on {new Date(selectedNote.supervisor_2_signed_at).toLocaleDateString()} at {new Date(selectedNote.supervisor_2_signed_at).toLocaleTimeString()}
                            </p>
                          )}
                          {(!selectedNote.supervisor_2_signed_at || selectedNote.supervisor_2_signed_date_hidden) && selectedNote.supervisor_2_signed_by && (
                            <p className="text-xs text-green-700 mt-1">Signed (date not recorded)</p>
                          )}
                          {selectedNote.supervisor_2_signed_by === user.id && (
                            <p className="text-xs text-gray-500 mt-1 italic">Right-click to remove your signature</p>
                          )}
                        </div>
                      ) : canSignAsSupervisor2 ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          data-signature-area="supervisor_2"
                          onClick={(e) => handleSignNote('supervisor_2', true, null, e.shiftKey)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSignatureContextMenuPos({ x: e.clientX, y: e.clientY });
                            setSignatureContextMenuType('supervisor_2');
                            setShowSignatureContextMenu(true);
                          }}
                        >
                          ✍️ Sign as Additional Supervisor
                          <span className="text-xs text-gray-500 ml-2">(Right-click for options)</span>
                        </Button>
                      ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded text-gray-500 text-sm">
                          Awaiting additional supervisor signature ({sup2Name})
                        </div>
                      )}
                        </div>
                      );
                    })()}

                    {/* Additional Signatures */}
                    {selectedNote && (() => {
                      const additionalSigs = selectedNote.additional_signatures || [];
                      const alreadySigned = additionalSigs.some(s => s.signer_id === user?.id);
                      const signAsOptions = [
                        { value: 'provider', label: 'Provider' },
                        { value: 'supervisor', label: 'Supervisor' },
                        { value: 'clinical_director', label: 'Clinical Director' },
                        { value: 'witness', label: 'Witness' },
                        { value: 'reviewer', label: 'Reviewer' },
                      ];
                      
                      return (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Label className="text-sm font-semibold">Additional Signatures</Label>
                            {/* "Why is this note routed here?" tooltip.
                                Rendered only when the routing rule engine
                                fired — for manually routed or legacy notes
                                (routing_rule_applied=null) we skip it to
                                avoid misleading anyone. Built as a plain
                                title= attribute for zero-dep hover and
                                so it works on every surface that renders
                                the note (no context menu needed). */}
                            {selectedNote.routing_rule_applied && (() => {
                              const r = selectedNote.routing_rule_applied;
                              const fieldLabels = {
                                license_status: 'Provider License Status',
                                medicare_approved: 'Provider Medicare Approved',
                                provider_id: 'Specific Provider',
                                provider_role: 'Provider Role',
                                billing_type: 'Insurance Billing Type',
                                insurance_company: 'Insurance Company',
                                template_requires_signature: 'Template Requires Signature',
                                template_billable: 'Template is Billable',
                              };
                              const opLabels = { equals: '=', not_equals: '≠', contains: 'contains' };
                              const lines = [
                                `Routed by rule: "${r.rule_name || 'unnamed'}"`,
                                ...(r.matched_conditions || []).map(c =>
                                  `  • ${fieldLabels[c.field] || c.field} ${opLabels[c.operator] || c.operator} ${c.value}`
                                ),
                              ];
                              return (
                                <span
                                  className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-bold cursor-help hover:bg-indigo-200"
                                  title={lines.join('\n')}
                                  data-testid="routing-rule-info"
                                  aria-label="Routing rule information"
                                >
                                  i
                                </span>
                              );
                            })()}
                          </div>
                          {additionalSigs.length > 0 && (
                            <div className="space-y-2 mb-2">
                              {additionalSigs.map((sig, idx) => (
                                <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg" data-signature-area="true">
                                  {/* Use SignatureImage so a stored
                                      ``gcs://`` scheme is resolved
                                      via the API instead of rendered
                                      as a broken-image icon (the
                                      production bug user
                                      screenshotted). */}
                                  {sig.signature_image && (
                                    <div className="mb-2 bg-white p-2 border border-gray-200 rounded inline-block">
                                      <SignatureImage
                                        raw={sig.signature_image}
                                        userId={sig.signer_id}
                                        alt={`${sig.signer_name} signature`}
                                        testId={`additional-signature-img-${sig.signer_id || idx}`}
                                      />
                                    </div>
                                  )}
                                  <p className="text-green-900 flex items-center text-sm font-semibold">
                                    <span className="mr-2">✅</span>
                                    {sig.signer_name}
                                    {sig.sign_as && <span className="text-green-700 font-normal ml-1 text-xs">({sig.sign_as.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())})</span>}
                                  </p>
                                  <p className="text-green-700 text-xs">{sig.signed_at ? `Signed on ${new Date(sig.signed_at).toLocaleDateString()} at ${new Date(sig.signed_at).toLocaleTimeString()}` : ''}</p>
                                  {(sig.signer_id === user?.id || isSuperAdmin(user)) && (
                                    <div className="mt-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`remove-additional-sig-${idx}`}
                                        onClick={async () => {
                                          // iframe-safe confirm (replaces native confirm() which
                                          // returns false silently inside Emergent's preview sandbox).
                                          const ok = await confirmAction(
                                            `Remove ${sig.signer_name}'s signature?`,
                                            { title: 'Remove additional signature?', confirmLabel: 'Remove', destructive: true }
                                          );
                                          if (!ok) return;
                                          try {
                                            await axios.delete(`${API}/notes/${selectedNote.id}/additional-signatures/${sig.signer_id}`);
                                            setSelectedNote(prev => ({
                                              ...prev,
                                              additional_signatures: (prev.additional_signatures || []).filter(s => s.signer_id !== sig.signer_id)
                                            }));
                                            toast.success('Signature removed');
                                          } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Failed to remove signature');
                                          }
                                        }}
                                      >
                                        Remove Signature
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {!alreadySigned && (
                            <div className="flex gap-2 flex-wrap">
                              {signAsOptions.map(opt => (
                                <Button
                                  key={opt.value}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs"
                                  data-testid={`add-sig-as-${opt.value}`}
                                  onClick={async () => {
                                    try {
                                      const resp = await axios.post(`${API}/notes/${selectedNote.id}/add-signature`, { sign_as: opt.value });
                                      const noteResp = await axios.get(`${API}/notes/${selectedNote.id}`);
                                      setSelectedNote(noteResp.data);
                                      // If the signer was recognized as the assigned
                                      // supervisor, surface that explicitly — it's a
                                      // common source of confusion when a supervisor
                                      // signs here instead of the dedicated box.
                                      if (resp.data?.recognized_as_supervisor) {
                                        toast.success(
                                          `Supervisor signature recorded${resp.data.new_status ? ` — note advanced to ${resp.data.new_status.replace(/_/g, ' ')}` : ''}`
                                        );
                                      } else {
                                        toast.success(resp.data.message || 'Signature added');
                                      }
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to add signature');
                                    }
                                  }}
                                >
                                  + Sign as {opt.label}
                                </Button>
                              ))}
                            </div>
                          )}
                          {alreadySigned && additionalSigs.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">You have already signed this document</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
              )}
            </div>
            );
          })()}
          
          {/* Assessment Reviews Section - ONLY for Comprehensive Assessment / CANS notes.
              Substring 'assessment' matching was too broad (e.g. Risk Assessment,
              'Assessments' filing category) and leaked this section onto other notes. */}
          {selectedNote && (() => {
            const templateName = formTemplates.find(t => t.id === selectedNote.template_id)?.name?.toLowerCase() || '';
            const noteType = selectedNote.note_type?.toLowerCase() || '';
            const formTypeName = selectedNote.form_type_name?.toLowerCase() || '';
            const storedTemplateName = selectedNote.template_name?.toLowerCase() || '';
            const isReviewable = (s) => /comprehensive assessment|comp assessment|\bcans\b/.test(s);
            return [noteType, formTypeName, storedTemplateName, templateName].some(isReviewable);
          })() && (
            <Card className="mt-4 border-indigo-200">
              <CardHeader className="py-3 bg-gradient-to-r from-indigo-50 to-purple-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <History className="h-4 w-4 text-indigo-600" />
                    Assessment Reviews
                  </CardTitle>
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={async () => {
                      try {
                        const today = new Date().toISOString().split('T')[0];
                        
                        // Get sections dynamically from the form template
                        const template = formTemplates.find(t => t.id === selectedNote.template_id);
                        let defaultSections = [];
                        
                        if (template?.form_fields) {
                          // Extract section headers from the template
                          defaultSections = template.form_fields
                            .filter(f => f.type === 'section_header')
                            .map(f => ({
                              section_name: f.label,
                              status: 'no_change',
                              notes: ''
                            }));
                        }
                        
                        // Fallback sections if no template found
                        if (defaultSections.length === 0) {
                          defaultSections = [
                            { section_name: 'Presenting Problem', status: 'no_change', notes: '' },
                            { section_name: 'Mental Status Examination', status: 'no_change', notes: '' },
                            { section_name: 'Diagnostic Impressions', status: 'no_change', notes: '' },
                            { section_name: 'Treatment Recommendations', status: 'no_change', notes: '' },
                            { section_name: 'Risk Assessment', status: 'no_change', notes: '' }
                          ];
                        }
                        
                        const response = await axios.post(`${API}/notes/${selectedNote.id}/reviews`, {
                          date_of_review: today,
                          section_changes: defaultSections,
                          summary: ''
                        });
                        
                        // Refresh the note
                        const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                        setSelectedNote(updatedNote.data);
                        toast.success('Review section added');
                      } catch (error) {
                        console.error('Error adding review:', error);
                        toast.error('Failed to add review section');
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Review
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Track periodic reviews and changes to this assessment</p>
              </CardHeader>
              <CardContent className="py-3">
                {(!selectedNote.reviews || selectedNote.reviews.length === 0) ? (
                  <div className="text-center py-6 text-gray-500">
                    <History className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No reviews yet</p>
                    <p className="text-xs mt-1">Click "Add Review" to document a periodic review</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedNote.reviews.map((review, reviewIndex) => (
                      <div key={review.id} className={`p-3 rounded border ${reviewIndex === 0 ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">📆 Review - {formatLocalDate(review.date_of_review)}</span>
                            {reviewIndex === 0 && <Badge className="bg-indigo-600 text-white text-xs">Latest</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            {review.provider_signed_by ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">✓ Signed</Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                                onClick={async () => {
                                  if (window.confirm('Delete this review? This cannot be undone.')) {
                                    try {
                                      await axios.delete(`${API}/notes/${selectedNote.id}/reviews/${review.id}`);
                                      const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                                      setSelectedNote(updatedNote.data);
                                      toast.success('Review deleted');
                                    } catch (error) {
                                      toast.error('Failed to delete review');
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Section Changes */}
                        <div className="space-y-2 mb-3">
                          {review.section_changes?.map((change, changeIndex) => (
                            <div key={changeIndex} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                              <span className={change.status === 'updated' ? 'text-orange-700 font-medium' : 'text-gray-700'}>
                                {change.section_name}
                              </span>
                              <div className="flex items-center gap-2">
                                {!review.provider_signed_by ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant={change.status === 'no_change' ? 'default' : 'outline'}
                                      className={`h-6 px-2 text-xs ${change.status === 'no_change' ? 'bg-green-600' : ''}`}
                                      onClick={async () => {
                                        const newChanges = [...review.section_changes];
                                        newChanges[changeIndex] = { ...change, status: 'no_change' };
                                        try {
                                          await axios.put(`${API}/notes/${selectedNote.id}/reviews/${review.id}`, {
                                            section_changes: newChanges
                                          });
                                          const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                                          setSelectedNote(updatedNote.data);
                                        } catch (error) {
                                          toast.error('Failed to update');
                                        }
                                      }}
                                    >
                                      No Change
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={change.status === 'updated' ? 'default' : 'outline'}
                                      className={`h-6 px-2 text-xs ${change.status === 'updated' ? 'bg-orange-600' : ''}`}
                                      onClick={async () => {
                                        const newChanges = [...review.section_changes];
                                        newChanges[changeIndex] = { ...change, status: 'updated' };
                                        try {
                                          await axios.put(`${API}/notes/${selectedNote.id}/reviews/${review.id}`, {
                                            section_changes: newChanges
                                          });
                                          const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                                          setSelectedNote(updatedNote.data);
                                        } catch (error) {
                                          toast.error('Failed to update');
                                        }
                                      }}
                                    >
                                      Updated
                                    </Button>
                                  </>
                                ) : (
                                  <Badge className={`text-xs ${change.status === 'no_change' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                    {change.status === 'no_change' ? '✓ No Change' : '✎ Updated'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Notes for updated sections */}
                        {review.section_changes?.filter(c => c.status === 'updated').map((change, i) => {
                          const textareaId = `review-${review.id}-change-${change.section_name}`;
                          return (
                          <div key={`${review.id}-${change.section_name}`} className="mb-2 p-2 bg-orange-50 rounded border border-orange-200">
                            <Label className="text-xs text-orange-700 font-medium">{change.section_name} - Change Notes:</Label>
                            {!review.provider_signed_by ? (
                              <Textarea
                                id={textareaId}
                                defaultValue={change.notes || ''}
                                placeholder="Describe what changed..."
                                className="mt-1 text-sm min-h-[60px]"
                                onBlur={async (e) => {
                                  try {
                                    const newChanges = [...review.section_changes];
                                    const idx = newChanges.findIndex(c => c.section_name === change.section_name);
                                    if (idx !== -1) {
                                      newChanges[idx] = { ...newChanges[idx], notes: e.target.value };
                                      await axios.put(`${API}/notes/${selectedNote.id}/reviews/${review.id}`, {
                                        section_changes: newChanges
                                      });
                                      // Update local state after successful save
                                      const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                                      setSelectedNote(updatedNote.data);
                                      toast.success('Notes saved');
                                    }
                                  } catch (error) {
                                    toast.error('Failed to save notes');
                                  }
                                }}
                              />
                            ) : (
                              <p className="text-sm text-gray-700 mt-1">{change.notes || 'No details provided'}</p>
                            )}
                          </div>
                          );
                        })}
                        
                        {/* Summary */}
                        <div className="mt-3">
                          <Label className="text-xs text-gray-500">Review Summary</Label>
                          {!review.provider_signed_by ? (
                            <Textarea
                              key={`summary-${review.id}`}
                              defaultValue={review.summary || ''}
                              placeholder="Overall summary of this review..."
                              className="mt-1 text-sm min-h-[60px]"
                              onBlur={async (e) => {
                                try {
                                  await axios.put(`${API}/notes/${selectedNote.id}/reviews/${review.id}`, {
                                    summary: e.target.value
                                  });
                                  toast.success('Summary saved');
                                } catch (error) {
                                  toast.error('Failed to save summary');
                                }
                              }}
                            />
                          ) : (
                            <p className="text-sm text-gray-700 mt-1 p-2 bg-gray-50 rounded">{review.summary || 'No summary provided'}</p>
                          )}
                        </div>
                        
                        {/* Signature — same SignatureDisplay as other documents
                            (Jun 2026); right-click or "Remove Signature" to
                            unsign (edit access only). */}
                        <div className="mt-3 pt-3 border-t">
                          {review.provider_signed_by ? (
                            <SignatureDisplay
                              value={{ signer_id: review.provider_signed_by, signed_at: review.provider_signed_at }}
                              users={users}
                              currentUser={user}
                              disabled={!canEditNote(selectedNote)}
                              testId={`review-signature-${review.id}`}
                              loadedSignatures={loadedSignatures}
                              setLoadedSignatures={setLoadedSignatures}
                              getSignatureUrl={getSignatureUrl}
                              onContextMenu={(e) => {
                                if (canEditNote(selectedNote)) {
                                  e.preventDefault();
                                  handleUnsignReview(review);
                                }
                              }}
                              onChange={(v) => { if (v === null) handleUnsignReview(review); }}
                            />
                          ) : (
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/notes/${selectedNote.id}/reviews/${review.id}/sign`, {
                                    signature: user?.full_name || user?.email
                                  });
                                  const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                                  setSelectedNote(updatedNote.data);
                                  toast.success('Review signed');
                                } catch (error) {
                                  toast.error('Failed to sign review');
                                }
                              }}
                            >
                              ✍️ Sign Review
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Linked-appointment bar removed (Oct 2026) — Open Appointment /
              Unlink now live in the header appointment row next to End time. */}
          
          {/* Created/Last Modified - At the very bottom with doc_number */}
          {/* Fallback Consent Affirmation - shows when note has telehealth_consent but template doesn't have consent field */}
          {selectedNote && selectedNote.telehealth_consent && 
           selectedNote.telehealth_consent.pos_code && 
           selectedNote.telehealth_consent.modality && 
           !isEditingNote && (() => {
            // Check if template has a consent_affirmation field - if so, it will be rendered inline
            const template = formTemplates.find(t => t.id === selectedNote.template_id);
            const hasConsentField = template?.form_fields?.some(f => f.type === 'consent_affirmation');
            if (hasConsentField) return null; // Template handles consent display
            
            const consent = selectedNote.telehealth_consent;
            const snapshot = selectedNote.consent_narrative_snapshot;
            const token = sessionStorage.getItem('token');
            const isSigned = !!selectedNote.client_signed_at;
            
            return (
              <div className="mt-4">
                <ConsentAffirmationDisplay 
                  consentData={consent} 
                  snapshot={snapshot}
                  token={token}
                  isSigned={isSigned}
                />
              </div>
            );
          })()}
          
          {selectedNote && (
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 space-y-1">
              {selectedNote.doc_number && (
                <p>
                  <span className="font-semibold">Document ID:</span> <span className="font-mono bg-gray-100 px-1 rounded">{selectedNote.doc_number}</span>
                </p>
              )}
              <p>
                <span className="font-semibold">Created:</span> {new Date(selectedNote.created_at).toLocaleDateString()} at {new Date(selectedNote.created_at).toLocaleTimeString()}
              </p>
              {selectedNote.updated_at && selectedNote.updated_at !== selectedNote.created_at && (
                <p>
                  <span className="font-semibold">Last Modified:</span> {new Date(selectedNote.updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
          
          {/* Bottom Save/Cancel Buttons - for convenience when at bottom of note */}
          {/* STOPLIGHT: Save button turns RED when blocked by autosave error */}
          {isEditingNote && (
            <div className="mt-4 pt-4 border-t border-gray-300 flex justify-end gap-2">
              <Button
                variant="outline"
                disabled={isSavingNote}
                onClick={() => {
                  setIsEditingNote(false);
                  setEditedNoteContent({});
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                className={autosaveBlockedReason
                  ? (isContentLossBlock
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-red-600 hover:bg-red-700 cursor-not-allowed opacity-75')
                  : 'bg-teal-600 hover:bg-teal-700'}
                disabled={isSavingNote || (!!autosaveBlockedReason && !isContentLossBlock)}
                title={autosaveBlockedReason || undefined}
                onClick={async () => {
                  if (isSavingNote) return;
                  if (autosaveBlockedReason) {
                    if (isContentLossBlock) setShowLossOverride(true);
                    return;
                  }
                  setIsSavingNote(true);
                  try {
                    const updateData = {
                      content: JSON.stringify(editedNoteContent),
                      date_of_service: selectedNote.date_of_service,
                      start_time: selectedNote.start_time,
                      end_time: selectedNote.end_time,
                      units: selectedNote.units,
                      cpt_code: selectedNote.cpt_code,
                      appointment_id: selectedNote.appointment_id,
                      linked_goals: linkedGoals,
                      // Echo guard: only send consent when actually changed vs load snapshot
                      ...(JSON.stringify(editedNoteConsent || null) !== JSON.stringify(selectedNote.telehealth_consent || null)
                        ? { telehealth_consent: editedNoteConsent }
                        : {}),
                      updated_at: new Date().toISOString()
                    };
                    await axios.put(`${API}/notes/${selectedNote.id}`, updateData);
                    toast.success('Note saved successfully');
                    // Clear error states on successful save
                    setNoteCreationError(null);
                    setAutosaveBlockedReason(null);
                    setIsEditingNote(false);
                    setEditedNoteConsent(null);
                    
                    // OPTIMISTIC UPDATE: Update notes lists immediately with new data
                    // This prevents the need for a full refresh and shows changes instantly
                    const optimisticUpdate = (note) => {
                      if (note.id === selectedNote.id) {
                        return {
                          ...note,
                          date_of_service: selectedNote.date_of_service,
                          start_time: selectedNote.start_time,
                          end_time: selectedNote.end_time,
                          units: selectedNote.units,
                          cpt_code: selectedNote.cpt_code,
                          updated_at: new Date().toISOString()
                        };
                      }
                      return note;
                    };
                    setNotes(prevNotes => prevNotes.map(optimisticUpdate));
                    setClientNotes(prevNotes => prevNotes.map(optimisticUpdate));
                    
                    // Background refresh to ensure consistency
                    refreshNotes(); refreshTodos();
                    const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                    setSelectedNote(updatedNote.data);
                  } catch (error) {
                    console.error('Save error:', error);
                    // LOUD FAILURE: Set error state to show banner
                    setNoteCreationError(`Failed to save: ${error.response?.data?.detail || error.message}`);
                    setAutosaveBlockedReason('Save failed - see error above');
                    toast.error(`Failed to update note: ${error.response?.data?.detail || error.message}`);
                  } finally {
                    setIsSavingNote(false);
                  }
                }}
              >
                {isSavingNote ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : autosaveBlockedReason ? (
                  <>
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    {isContentLossBlock ? 'Save Blocked — Review' : 'Save Blocked'}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Document Footer - REMOVED - metadata now consolidated in top-left footer */}
          
          {/* Reference Note Side Panel */}
          {showReferencePanel && (
            <ReferenceNotePanel
              selectedNote={selectedNote}
              notes={notes}
              users={users}
              formTemplates={formTemplates}
              clientDiagnoses={clientDiagnoses}
              locations={locations}
              onClose={() => { setShowReferencePanel(false); }}
              onApplyField={(fieldId, value, label) => {
                if (!isEditingNote) {
                  setIsEditingNote(true);
                }
                // Handle consent affirmation — stored in editedNoteConsent, not editedNoteContent
                if (label === 'Consent Affirmation' && typeof value === 'object' && value !== null && value.pos_code) {
                  setEditedNoteConsent(prev => ({ ...(prev || {}), ...value }));
                  return;
                }
                const current = editedNoteContent;
                if (!current) {
                  const text = Array.isArray(value) ? value.join(', ') : String(value || '');
                  navigator.clipboard.writeText(text).catch(() => {});
                  toast.info('No note content loaded — copied to clipboard');
                  return;
                }
                let matchedFieldId = null;
                if (current.fields && Array.isArray(current.fields)) {
                  const match = current.fields.find(f => f.id === fieldId) || current.fields.find(f => f.label === label);
                  if (match) matchedFieldId = match.id;
                }
                if (!matchedFieldId && current.sections && Array.isArray(current.sections)) {
                  for (const s of current.sections) {
                    const match = (s.fields || []).find(f => f.id === fieldId) || (s.fields || []).find(f => f.label === label);
                    if (match) { matchedFieldId = match.id; break; }
                  }
                }
                if (!matchedFieldId) {
                  const text = Array.isArray(value) ? value.join(', ') : String(value || '');
                  navigator.clipboard.writeText(text).catch(() => {});
                  toast.info('No matching field — copied to clipboard');
                  return;
                }
                setEditedNoteContent(prev => {
                  if (!prev) return prev;
                  if (prev.fields && Array.isArray(prev.fields)) {
                    return { ...prev, fields: prev.fields.map(f => f.id === matchedFieldId ? { ...f, value } : f) };
                  }
                  if (prev.sections && Array.isArray(prev.sections)) {
                    return { ...prev, sections: prev.sections.map(s => ({
                      ...s,
                      fields: (s.fields || []).map(f => f.id === matchedFieldId ? { ...f, value } : f)
                    })) };
                  }
                  return prev;
                });
              }}
              onApplyAll={(fields) => {
                if (!isEditingNote) {
                  setIsEditingNote(true);
                }
                // Separate consent fields from regular fields
                const consentField = fields.find(f => f.type === 'consent_affirmation');
                const regularFields = fields.filter(f => f.type !== 'consent_affirmation');
                
                // Apply consent
                if (consentField && typeof consentField.value === 'object') {
                  setEditedNoteConsent(prev => ({ ...(prev || {}), ...consentField.value }));
                }
                
                // Apply regular fields
                if (regularFields.length > 0) {
                  setEditedNoteContent(prev => {
                    if (!prev) return prev;
                    if (prev.fields && Array.isArray(prev.fields)) {
                      let newFields = [...prev.fields];
                      regularFields.forEach(({ id, value, label }) => {
                        const idx = newFields.findIndex(f => f.id === id || f.label === label);
                        if (idx >= 0) newFields[idx] = { ...newFields[idx], value };
                      });
                      return { ...prev, fields: newFields };
                    }
                    if (prev.sections && Array.isArray(prev.sections)) {
                      const fieldMap = new Map(regularFields.map(f => [f.id, f.value]));
                      const labelMap = new Map(regularFields.map(f => [f.label, f.value]));
                      return { ...prev, sections: prev.sections.map(s => ({
                        ...s,
                        fields: (s.fields || []).map(f => {
                          if (fieldMap.has(f.id)) return { ...f, value: fieldMap.get(f.id) };
                          if (labelMap.has(f.label)) return { ...f, value: labelMap.get(f.label) };
                          return f;
                        })
                      })) };
                    }
                    return prev;
                  });
                }
              }}
            />
          )}
          {/* Edit Note Context Menu - Rendered INSIDE DialogContent to bypass onInteractOutside */}
          {/* Edit Context Menu - Using Portal to escape dialog's transform context */}
          {showEditContextMenu && ReactDOM.createPortal(
            <>
              <div 
                className="fixed inset-0 z-[999999]" 
                onClick={() => setShowEditContextMenu(false)}
              />
              <div 
                className="fixed z-[1000000] bg-white border border-gray-300 rounded-lg shadow-xl py-1 min-w-48 pointer-events-auto"
                style={{ 
                  left: `${Math.min(editContextMenuPos.x, window.innerWidth - 220)}px`, 
                  top: `${Math.min(editContextMenuPos.y, window.innerHeight - 200)}px`
                }}
              >
                <button
                  className={`w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center pointer-events-auto ${
                    autosaveBlockedReason ? 'text-red-600 cursor-not-allowed opacity-75' : 'text-teal-700'
                  }`}
                  disabled={!!autosaveBlockedReason && !isContentLossBlock}
                  title={autosaveBlockedReason || undefined}
                  onClick={async () => {
                    if (autosaveBlockedReason) {
                      if (isContentLossBlock) { setShowEditContextMenu(false); setShowLossOverride(true); }
                      return;
                    }
                    setShowEditContextMenu(false);
                    try {
                      const updateData = {
                        content: editedNoteContent,
                        updated_at: new Date().toISOString()
                      };
                      await axios.put(`${API}/notes/${selectedNote.id}`, updateData);
                      toast.success('Note saved successfully');
                      // Clear error states on successful save
                      setNoteCreationError(null);
                      setAutosaveBlockedReason(null);
                      setIsEditingNote(false);
                      refreshNotes(); refreshTodos();
                      const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
                      setSelectedNote(updatedNote.data);
                    } catch (error) {
                      console.error('Save error:', error);
                      // LOUD FAILURE: Set error state to show banner
                      setNoteCreationError(`Failed to save: ${error.response?.data?.detail || error.message}`);
                      setAutosaveBlockedReason('Save failed - see error above');
                      toast.error(`Failed to update note: ${error.response?.data?.detail || error.message}`);
                    }
                  }}
                >
                  {autosaveBlockedReason ? (
                    <>
                      <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                      Save Blocked
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  className={`w-full text-left px-4 py-2 hover:bg-teal-50 flex items-center pointer-events-auto ${
                    autosaveBlockedReason ? 'text-red-600 cursor-not-allowed opacity-75' : 'text-teal-600'
                  }`}
                  disabled={!!autosaveBlockedReason && !isContentLossBlock}
                  title={autosaveBlockedReason || undefined}
                  onClick={async () => {
                    if (autosaveBlockedReason) {
                      if (isContentLossBlock) { setShowEditContextMenu(false); setShowLossOverride(true); }
                      return;
                    }
                    setShowEditContextMenu(false);
                    try {
                      const updateData = {
                        content: editedNoteContent,
                        updated_at: new Date().toISOString()
                      };
                      await axios.put(`${API}/notes/${selectedNote.id}`, updateData);
                      toast.success('Note saved and closed');
                      // Clear error states on successful save
                      setNoteCreationError(null);
                      setAutosaveBlockedReason(null);
                      setIsEditingNote(false);
                      setEditedNoteContent({});
                      setShowNoteDetail(false);
                      setSelectedNote(null);
                      refreshNotes(); refreshTodos();
                    } catch (error) {
                      console.error('Save error:', error);
                      // LOUD FAILURE: Set error state to show banner
                      setNoteCreationError(`Failed to save: ${error.response?.data?.detail || error.message}`);
                      setAutosaveBlockedReason('Save failed - see error above');
                      toast.error(`Failed to save note: ${error.response?.data?.detail || error.message}`);
                    }
                  }}
                >
                  {autosaveBlockedReason ? (
                    <>
                      <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                      Save & Close Blocked
                    </>
                  ) : (
                    <>
                      <LogOut className="h-4 w-4 mr-2" />
                      Save & Close
                    </>
                  )}
                </button>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-gray-600 pointer-events-auto"
                  onClick={() => {
                    setShowEditContextMenu(false);
                    setIsEditingNote(false);
                    setEditedNoteContent({});
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel Editing
                </button>
              </div>
            </>,
            document.body
          )}
          {/* Signature Context Menu - Using Portal to escape dialog's transform context */}
          {showSignatureContextMenu && signatureContextMenuType && ReactDOM.createPortal(
            <>
              <div 
                className="fixed inset-0 z-[999999]" 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSignatureContextMenu(false);
                }}
              />
              <div 
                className="fixed z-[1000000] bg-white border border-gray-300 rounded-lg shadow-xl py-1 min-w-48 pointer-events-auto"
                style={{ 
                  left: `${Math.min(signatureContextMenuPos.x, window.innerWidth - 220)}px`, 
                  top: `${Math.min(signatureContextMenuPos.y, window.innerHeight - 200)}px`
                }}
              >
                {(() => {
                  // Check if note is already signed by current user
                  const isSignedByMe = selectedNote && user && 
                                       selectedNote[`${signatureContextMenuType}_signed_by`] === user.id;
                  
                  return isSignedByMe ? (
                    <>
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-red-600 pointer-events-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSignatureContextMenu(false);
                          handleUnsignNote(signatureContextMenuType);
                        }}
                      >
                        <span className="mr-2">❌</span>
                        Remove my signature
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center pointer-events-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSignatureContextMenu(false);
                          handleSignNote(signatureContextMenuType, true, null, e.shiftKey);
                        }}
                      >
                        <span className="mr-2">✍️</span>
                        Sign with date
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center pointer-events-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSignatureContextMenu(false);
                          handleSignNote(signatureContextMenuType, false, null, e.shiftKey);
                        }}
                      >
                        <span className="mr-2">📝</span>
                        Sign without date
                      </button>
                      {/* Jun 2026 — routing override (user request): supervisors/
                          admins/superadmins can stamp a supervisor signature even
                          when they aren't the assigned supervisor (backend honors
                          force_sign for the supervisor path). */}
                      {(signatureContextMenuType === 'supervisor' || signatureContextMenuType === 'supervisor_2') &&
                        (['supervisor', 'admin', 'superadmin', 'super admin'].includes(userRoleName) ||
                          user?.is_supervisor || isSuperAdmin(user)) && (
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center pointer-events-auto"
                          data-testid="sign-override-routing-option"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSignatureContextMenu(false);
                            handleSignNote(signatureContextMenuType, true, null, true);
                          }}
                        >
                          <span className="mr-2">⚡</span>
                          Sign anyway (override routing)
                        </button>
                      )}
                      {typeof openSignatureConfirm === 'function' && (
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center pointer-events-auto"
                          data-testid="sign-custom-datetime-option"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSignatureContextMenu(false);
                            openSignatureConfirm(signatureContextMenuType);
                          }}
                        >
                          <span className="mr-2">🗓️</span>
                          Sign with custom date/time…
                        </button>
                      )}
                    </>
                  );
                })()}
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-gray-500 pointer-events-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSignatureContextMenu(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </>,
            document.body
          )}
        </DialogContent>
      </Dialog>
      )}
      {/* ── Large-content-change override confirmation (Jun 2026) ── */}
      <Dialog open={showLossOverride} onOpenChange={setShowLossOverride}>
        <DialogContent className="max-w-md" data-testid="content-loss-override-dialog">
          <DialogHeader>
            <h3 className="text-lg font-semibold flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Significant content change
            </h3>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              This save removes{lossOverridePercent ? ` about ${lossOverridePercent}% of` : ' a large portion of'} this
              note's previously saved content, so automatic saving was paused as a safety measure.
            </p>
            <p>
              If this change is <strong>intentional</strong> (for example, you're rewriting or trimming the note),
              you can save anyway. The previous version is kept on the server and can be restored by an administrator.
            </p>
            <p className="text-xs text-slate-500">This override is recorded in the audit log.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowLossOverride(false)} data-testid="loss-override-cancel">
              Keep editing
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={performOverrideSave}
              disabled={isSavingNote}
              data-testid="loss-override-save-anyway"
            >
              {isSavingNote ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Save anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
